import { getMountedRoomAudioRendererDiagnostics } from "@/components/livekit/TrackedRoomAudioRenderer";
import { useAppData } from "@/contexts/AppDataContext";
import { trackFirstConversationCompleted } from "@/lib/analytics/gtm-datalayer";
import { resolveEffectiveAuthUser } from "@/lib/auth-utils";
import { getOrCreateClientId } from "@/lib/clientId";
import type {
  GuestSpatiusAvatarExperimentProperties,
  GuestSpatiusAvatarExposure,
} from "@/lib/experiments/guestSpatiusAvatar";
import {
  guestVoiceAnalyticsAllowed,
  prepareGuestVoiceStart,
  registerGuestVoiceAnalytics,
} from "@/lib/experiments/guestVoiceRuntimeClient";
import type { WebappSignupBeforeDemoAttributionProperties } from "@/lib/experiments/webappSignupBeforeDemo";
import { type FreemiumCohort } from "@/lib/freemium/usage-state";
import { waitForActiveGuestSessionClaim } from "@/lib/guestSessionClaim";
import type { StandardLearningMode } from "@/lib/learning-mode";
import { holdLessonRefreshUntil, withLessonRefreshProtection } from "@/lib/lesson-refresh-guard";
import {
  type BuiltConnectionDetailsRequest,
  buildConnectionDetailsRequest,
} from "@/lib/livekit-agent-connection-request";
import {
  getBrowserSessionDiagnostics,
  getLanguageNameByCode,
  getServerUrlHost,
} from "@/lib/livekit-agent-telemetry";
import {
  type LiveKitAgentTokenCacheEntry,
  type LiveKitConnectionDetailsTokenPayload,
  buildLiveKitAgentTokenCacheEntry,
  isLiveKitAgentTokenCacheEntryValid,
  toLiveKitConnectionDetailsTokenPayload,
} from "@/lib/livekit-agent-token-cache";
import {
  GUEST_USAGE_TIME_KEY,
  GUEST_USAGE_TODAY_KEY,
  MAX_REGISTERED_SECONDS_PER_DAY,
  getDateScopedUsageSeconds,
  getGuestTotalUsageSeconds,
  getRegisteredUsageSeconds,
  markPreSessionOfferDismissedToday,
  wasUserCreatedToday,
} from "@/lib/livekit-agent-usage-storage";
import { startRoomAudioPlayback } from "@/lib/livekit-audio-playback";
import {
  type LiveKitConnectCancelOrigin,
  decideLiveKitConnectFailure,
  getErrorMessage,
} from "@/lib/livekit-connect-failure-policy";
import {
  CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
  ConnectionDetailsFetchError,
  fetchConnectionDetailsWithRetry,
} from "@/lib/livekit-connection-details-fetch";
import { recoverUnauthorizedConnectionDetails } from "@/lib/livekit-connection-details-recovery";
import {
  captureLiveKitConnectionEvent as captureLiveKitConnectionEventTelemetry,
  isMicrophonePermissionError,
} from "@/lib/livekit-connection-event-telemetry";
import {
  collectActiveRemoteAudioPublications,
  inspectDuplicateRemoteAudioPublications,
} from "@/lib/livekit-duplicate-audio-guard";
import {
  decideVoiceSessionIssuanceRetry,
  isVoiceSessionReplacementSafe,
  parseVoiceSessionIssuanceInProgressRetry,
  shouldRetainVoiceSessionIssuance,
} from "@/lib/livekit-issuance-retry-policy";
import {
  rememberLiveKitRoomParticipantToken,
  rememberLiveKitRoomSessionProof,
} from "@/lib/livekit-room-participant-token";
import { hasActiveLiveKitConnection } from "@/lib/livekit-session-guards";
import {
  type SessionTransportShutdownHandle,
  StaleLiveKitConnectionAttemptError,
  assertCurrentLiveKitConnectionAttempt,
  createSessionTransportShutdownCoordinator,
  resolveSpatiusViewerBootstrapFailure,
} from "@/lib/livekit-session-lifecycle";
import {
  SessionRecoveryRoomStillExistsError,
  requestServerSessionShutdownForConnectionDetails,
  requestServerSessionShutdown as requestServerSessionShutdownRequest,
  sendSessionControlShutdownMessage,
} from "@/lib/livekit-session-shutdown";
import { recordSuccessfulSessionConnectAnalytics } from "@/lib/livekit-session-start-analytics";
import { type LiveKitSessionUsage, parseFreeVoiceLimitResponse } from "@/lib/livekit-session-usage";
import {
  AUDIO_MICROPHONE_PERMISSION_MESSAGE,
  AUDIO_PLAYBACK_BLOCKED_MESSAGE,
  SESSION_RECOVERY_UNCONFIRMED_MESSAGE,
  getUserFacingConnectionFailureMessage,
} from "@/lib/livekit-user-facing-messages";
import { createLogger } from "@/lib/logger";
import { createSessionLatencyTraceId } from "@/lib/sessionLatency";
import {
  type SessionAnalyticsSurface,
  type SessionLaunchSurface,
  type SessionPrewarmPolicy,
} from "@/lib/sessionLaunchPresets";
import {
  DEFAULT_SESSION_MODE,
  type SessionMode,
  UnsupportedSessionModeError,
  getSupportedSessionMode,
  normalizeSessionMode,
} from "@/lib/sessionRouting";
import {
  type SessionVocabularyContext,
  getSessionVocabularyCacheKey,
  serializeSessionVocabularyContext,
} from "@/lib/sessionVocabulary";
import {
  cleanupSpatiusSdkAudioElements,
  silenceSpatiusSdkAudioElementsForLipSync,
} from "@/lib/spatius-sdk-audio-cleanup";
import { supabase } from "@/lib/supabaseClient";
import { resolveExamplesBeforePractice } from "@/shared/example-first-preference";
import { ConnectionQuality, ConnectionState, Room, RoomEvent, Track } from "livekit-client";
// Import useAuth

import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Import language data
import { useAuth } from "../components/AuthContext";
import {
  isSpatiusAvatarIdentity,
  isSpatiusProvider,
  shouldKeepSpatiusPrimarySessionOnFallback,
  shouldResetSpatiusPrimarySessionBeforeConnect,
  shouldSubscribeSpatiusViewerPublication,
} from "./spatius-primary-session-policy";
import { useLessonRefreshProtection } from "./useLessonRefreshProtection";
import { useLiveKitAudioTrackDiagnostics } from "./useLiveKitAudioTrackDiagnostics";
import { useLiveKitBaseRoomEffects } from "./useLiveKitBaseRoomEffects";
import { useLiveKitBrowserLifecycleDiagnostics } from "./useLiveKitBrowserLifecycleDiagnostics";
import { useLiveKitConnectionStateMonitor } from "./useLiveKitConnectionStateMonitor";
import { useLiveKitConnectionTiming } from "./useLiveKitConnectionTiming";
import { useLiveKitFreemiumGate } from "./useLiveKitFreemiumGate";
import { useLiveKitRecordingTimer } from "./useLiveKitRecordingTimer";
import { useLiveKitSpatiusPrimaryEvents } from "./useLiveKitSpatiusPrimaryEvents";
import { useScreenWakeLock } from "./useScreenWakeLock";

const logger = createLogger("useLiveKitAgent");
const TOKEN_CACHE_VERSION = 5; // Level-based defaults and level-specific prompts need fresh rooms.
const FIRST_CONVERSATION_COMPLETED_KEY = "chicky:first_conversation_completed";
const FIRST_CONVERSATION_MIN_SECONDS = 60;
const SERVER_SESSION_SHUTDOWN_TIMEOUT_MS = 5000;
const SERVER_SESSION_SHUTDOWN_KEEPALIVE_TIMEOUT_MS = 4000;
const LOCAL_SESSION_ISOLATION_TIMEOUT_MS = 750;
const SESSION_CONTROL_SHUTDOWN_TIMEOUT_MS = 500;
const SPATIUS_PRIMARY_FALLBACK_DELAY_MS = 7000;
// The avatar egress publication can arrive only after the viewer connects,
// `avatar_ready` releases the greeting, TTS starts, and Spatius joins the base room.
// Keep this long enough that normal startup is not misclassified as missing audio.
const SPATIUS_PRIMARY_BASE_AUDIO_WATCHDOG_DELAY_MS = 7000;
const MAX_CONNECT_AUTO_RETRIES = 2;
const FRESH_CONNECTION_TIMEOUT_MS = 30000;
// Define connection states for better tracking
export type AgentConnectionState =
  | "disconnected"
  | "disconnecting"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed"
  | "ready";

export type TimeLimitPopupContext = {
  limitType: "guest" | "daily" | "weekly";
  freemiumCohort?: FreemiumCohort;
  qualifiedConversationCount?: number;
  qualifiedConversationsThisWeek?: number;
};

type SpatiusBootstrapResponse = {
  livekitUrl: string;
  roomName: string;
  participantToken?: string | null;
  spatius: {
    appId: string;
    avatarId: string;
    sessionToken: string;
    region?: string;
  };
};

type SessionConnectionDetails = {
  participantToken: string;
  roomName: string;
  serverUrl: string;
  participantName?: string;
  shutdownCapability?: string;
  tokenSource?: "prewarm" | "fresh";
  metered?: boolean;
  recoveryScope?: string;
  ownerId?: string | null;
  requiresRecovery?: boolean;
};

type LiveKitConnectionFailurePhase =
  | "idle"
  | "connection_details_fetch"
  | "connection_details_http"
  | "livekit_room_connect"
  | "microphone_enable"
  | "spatius_primary_bootstrap"
  | "spatius_primary_connect"
  | "initial_greeting_playout"
  | "managed_session";

type SessionEndedCapture = {
  roomName: string | null;
  capturedAtMs: number;
};

type SpatiusPrimaryFallbackReason = "error" | "timeout" | "missing_base_audio";

export interface SpatiusPrimarySession {
  connection: {
    roomName: string;
    token: string;
    url: string;
  };
  mode: SessionMode;
  spatius: SpatiusBootstrapResponse["spatius"];
}

interface UseLiveKitAgentProps {
  selectedLanguageCode: string;
  sessionLanguageName?: string;
  selectedLanguageLevel: string;
  selectedVoice: string;
  avatarId?: string;
  selectedTeachingStyle: string;
  nickname?: string;
  customInstructions?: string;
  allowUnauthenticatedSessions?: boolean;
  guestSessionLimitSeconds?: number;
  noAvatar?: boolean;
  fromLanguageName?: string; // Added: User's native language name
  fromLanguageCode?: string; // Added: User's native language BCP-47 code
  sessionMode?: SessionMode;
  learningMode?: StandardLearningMode;
  mathsDemoId?: string;
  mathsFixtureRevision?: string;
  tutorWhiteboardEnabled?: boolean;
  examplesBeforePractice?: boolean;
  setInteractionMode: (mode: "passive" | "text" | "voice") => void;
  // Tri-state access from RevenueCat. `null`/`undefined` is still unresolved and
  // must not be treated as verified free access by client-side offer/limit UI.
  subscribed?: boolean | null;
  classId?: string; // NEW: Teacher class ID for customized sessions
  teacherPreferences?: any; // NEW: Teacher preferences payload for prompt customization
  tutorId?: string; // NEW: Specific tutor ID
  tutorPrompt?: string; // NEW: Override prompt for specific tutor
  tutorCurriculumItemId?: string | null;
  selectedModel?: "pro" | "stealth" | "legacy"; // NEW: Agent model selection
  journeyChapterId?: string | null;
  podcastDiscussionId?: string | null;
  sessionVocabularyContext?: SessionVocabularyContext | null;
  conversationId?: string | null;
  resumeFromConversationId?: string | null;
  analyticsSurface?: SessionAnalyticsSurface;
  embedAnalyticsEventPrefix?: string;
  enableFreemiumPreSessionOffer?: boolean;
  launchSurface?: SessionLaunchSurface;
  prewarmPolicy?: SessionPrewarmPolicy;
  getGuestSpatiusAvatarExperimentExposure?: () => GuestSpatiusAvatarExposure | null;
  webappSignupBeforeDemoExperimentEligible?: boolean;
  getWebappSignupBeforeDemoExperimentProperties?: () =>
    | WebappSignupBeforeDemoAttributionProperties
    | Record<string, never>;
  onFreemiumPreSessionOfferDismissed?: () => void;
}

const useLiveKitAgent = ({
  selectedLanguageCode,
  sessionLanguageName,
  selectedLanguageLevel,
  selectedVoice,
  avatarId,
  selectedTeachingStyle,
  nickname = "",
  customInstructions = "",
  allowUnauthenticatedSessions = false,
  guestSessionLimitSeconds = 330,
  noAvatar = false,
  fromLanguageName, // Added
  fromLanguageCode, // Added
  sessionMode,
  learningMode,
  mathsDemoId,
  mathsFixtureRevision,
  tutorWhiteboardEnabled = false,
  examplesBeforePractice = resolveExamplesBeforePractice(undefined, selectedLanguageLevel),
  setInteractionMode,
  subscribed,
  classId, // NEW
  teacherPreferences, // NEW
  tutorId, // NEW
  tutorPrompt, // NEW
  tutorCurriculumItemId,
  selectedModel = "pro", // Default to pro
  journeyChapterId,
  podcastDiscussionId,
  sessionVocabularyContext,
  conversationId,
  resumeFromConversationId,
  analyticsSurface = "other",
  embedAnalyticsEventPrefix,
  enableFreemiumPreSessionOffer = false,
  launchSurface = "standard",
  prewarmPolicy = "normal",
  getGuestSpatiusAvatarExperimentExposure,
  webappSignupBeforeDemoExperimentEligible = false,
  getWebappSignupBeforeDemoExperimentProperties,
  onFreemiumPreSessionOfferDismissed,
}: UseLiveKitAgentProps) => {
  const configuredAvatarProvider = (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam")
    .trim()
    .toLowerCase();
  const shouldUseSpatiusPrimaryRoom = isSpatiusProvider(configuredAvatarProvider) && !noAvatar;
  const guestSpatiusAvatarExperimentPropertiesRef =
    useRef<GuestSpatiusAvatarExperimentProperties | null>(null);
  const createBaseRoom = useCallback(
    () =>
      new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      }),
    []
  );
  const [room, setRoom] = useState(createBaseRoom);
  const [sessionTransportShutdownCoordinator] = useState(() =>
    createSessionTransportShutdownCoordinator<Room>()
  );
  const [externalRoom, setExternalRoom] = useState<Room | null>(null);
  const [spatiusPrimarySession, setSpatiusPrimarySession] = useState<SpatiusPrimarySession | null>(
    null
  );
  const [spatiusPrimaryAudioFallbackActive, setSpatiusPrimaryAudioFallbackActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [activeSessionUsage, setActiveSessionUsage] = useState<LiveKitSessionUsage | null>(null);
  const [agentState, setAgentState] = useState<string>("disconnected");
  const { user, clearAuthState, openAuthModal } = useAuth(); // Get user and clearAuthState from AuthContext
  const { memories, qdrantContext, getMemoriesSummary } = useAppData(); // Get pre-loaded data
  const posthogClient = usePostHog(); // Get PostHog client via hook
  // Add connection state tracking
  const [connectionState, setConnectionState] = useState<AgentConnectionState>("disconnected");
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    ConnectionQuality.Unknown
  );
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [audioTrackError, setAudioTrackError] = useState<string | null>(null);
  const [audioRetryCount, setAudioRetryCount] = useState<number>(0);
  // Add auth retry tracking
  const [isRetryingAsGuest, setIsRetryingAsGuest] = useState<boolean>(false);
  const currentRoomRef = useRef<Room>(room);
  const isConnectedRef = useRef(false);
  const isRecordingRef = useRef(false);
  const liveKitCleanupCallbacksRef = useRef<{
    shutdownActiveSessionTransport: (
      source: string,
      options?: {
        keepalive?: boolean;
        timeoutMs?: number;
        localIsolationTimeoutMs?: number;
        sessionControlTimeoutMs?: number;
        room?: Room;
        attemptId?: number;
      }
    ) => SessionTransportShutdownHandle;
    clearSpatiusPrimaryFallbackTimer: () => void;
    clearConnectionElapsedTimer: () => void;
    resetLatencyTrace: () => void;
  } | null>(null);
  const baseRoomRef = useRef<Room>(room);
  const activeLocalIsolationPromiseRef = useRef<Promise<void> | null>(null);
  const activeSessionShutdownRef = useRef<{
    room: Room;
    attemptId: number;
    handle: SessionTransportShutdownHandle;
  } | null>(null);
  const spatiusPrimarySessionVersionRef = useRef(0);
  const spatiusPrimaryViewerRoomRef = useRef<Room | null>(null);
  const spatiusPrimaryParticipantConnectedHandlerRef = useRef<(() => void) | null>(null);
  const spatiusPrimaryTrackPublishedHandlerRef = useRef<(() => void) | null>(null);
  const spatiusPrimaryTrackSubscribedHandlerRef = useRef<(() => void) | null>(null);
  const spatiusPrimaryConnectRef = useRef<{
    details: SessionConnectionDetails;
    mode: SessionMode;
  } | null>(null);
  const spatiusPrimaryFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const spatiusPrimaryBaseAudioWatchdogTimerRef = useRef<NodeJS.Timeout | null>(null);
  const spatiusPrimaryConnectedRef = useRef(false);
  const spatiusPrimaryFallbackInProgressRef = useRef(false);
  const spatiusPrimaryAudioFallbackActiveRef = useRef(false);
  const activeAudioSessionGenerationRef = useRef(0);
  const lastAudioDiagnosticsPostHogAtRef = useRef(0);
  const lastSessionEndedCaptureRef = useRef<SessionEndedCapture | null>(null);
  const connectionFailurePhaseRef = useRef<LiveKitConnectionFailurePhase>("idle");
  const activeConnectionDetailsRef = useRef<SessionConnectionDetails | null>(null);
  const activeSessionUsageRef = useRef<LiveKitSessionUsage | null>(null);
  const handledConnectionExceptionReportedRef = useRef(false);

  // Track 401-specific retry state: 0=none, 1=same-token retry, 2=fresh-token retry
  const auth401RetryPhaseRef = useRef<0 | 1 | 2>(0);
  const MAX_401_RETRY_PHASES = 2; // Phase 1: same token, Phase 2: fresh token

  // Add a ref to track connection attempts to prevent multiple simultaneous connections
  const isConnectingRef = useRef(false);
  // Add timeout ref for connection timeout
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track retry attempts and the last requested mode to support auto-retry on timeout
  const connectRetryCountRef = useRef(0);
  const retryIssuanceStartAttemptIdRef = useRef<string | null>(null);
  const unresolvedStartRef = useRef<{
    id: string;
    scope: string;
    request: BuiltConnectionDetailsRequest;
  } | null>(null);
  const recoveryConnectRef = useRef<Promise<boolean> | null>(null);
  const getRecoveryScopeRef = useRef<(mode: SessionMode) => string>(() => "");
  getRecoveryScopeRef.current = (mode) =>
    JSON.stringify([
      user?.id || user?.email || null,
      mode,
      selectedLanguageCode,
      selectedLanguageLevel,
      selectedVoice,
      selectedTeachingStyle,
      avatarId,
      noAvatar,
      nickname,
      customInstructions,
      learningMode,
      fromLanguageCode,
      fromLanguageName,
      selectedModel,
      tutorId,
      tutorPrompt,
      tutorCurriculumItemId,
      journeyChapterId,
      podcastDiscussionId,
      conversationId,
      resumeFromConversationId,
      classId,
      teacherPreferences,
      tutorWhiteboardEnabled,
      examplesBeforePractice,
      mathsDemoId,
      mathsFixtureRevision,
      sessionVocabularyContext,
    ]);

  const lastRequestedModeRef = useRef<SessionMode>("conversation");
  const connectAttemptSeqRef = useRef(0);
  const activeConnectAttemptIdRef = useRef(0);
  const connectCancelOriginRef = useRef<{
    attemptId: number;
    origin: LiveKitConnectCancelOrigin;
  } | null>(null);
  const connectionFailureStickyRef = useRef(false);
  // Track if engaged session (5+ min) conversion has fired
  const hasTrackedEngagedSessionRef = useRef<boolean>(false);
  const hasTrackedGuestLimitReachedRef = useRef<boolean>(false);
  const hasTrackedGuestAuthenticatedSessionStartedRef = useRef<boolean>(false);
  // Track WebSocket 1006 reconnection attempts (separate from general retry)
  const websocket1006RetryCountRef = useRef(0);
  const MAX_WEBSOCKET_1006_RETRIES = 3;
  const freshReconnectInProgressRef = useRef(false);
  const freshReconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const connectToLiveKitCommandRef = useRef<(mode: SessionMode) => Promise<boolean>>(
    async () => false
  );
  const authTokenCacheIdentityRef = useRef<string | null>(null);
  // 🎓 TUTOR MARKETPLACE: Track latest tutorPrompt/tutorId to avoid stale closures
  // Add cache for connection tokens
  const tokenCacheRef = useRef<LiveKitAgentTokenCacheEntry | null>(null);
  const {
    connectionElapsedSeconds,
    latencyTraceRef,
    latencyAgentStateMarksRef,
    firstRemoteAudioMarkedRef,
    clearConnectionElapsedTimer,
    startConnectionElapsedTimer,
    freezeConnectionElapsedTimer,
    resetConnectionElapsedTimer,
    resetLatencyTrace,
    startLatencyTrace,
    markLatencyPhase,
  } = useLiveKitConnectionTiming({
    selectedLanguageCode,
    selectedVoice,
    avatarId,
    noAvatar,
    selectedModel,
    logger,
  });

  const MAX_GUEST_SECONDS = guestSessionLimitSeconds;
  const {
    showTimeLimitPopup,
    setShowTimeLimitPopup,
    timeLimitPopupContext,
    freemiumUsageState,
    setFreemiumUsageState,
    showFreemiumPreSessionOffer,
    setShowFreemiumPreSessionOffer,
    hasExceededGuestLimit,
    setHasExceededGuestLimit,
    hasExceededRegisteredLimit,
    setHasExceededRegisteredLimit,
    pendingPreSessionOfferModeRef,
    showFreemiumLimitPopup,
    evaluateFreemiumStartGate,
  } = useLiveKitFreemiumGate({
    user,
    subscribed,
    maxGuestSeconds: MAX_GUEST_SECONDS,
    analyticsSurface,
    posthogClient,
    authTokenCacheIdentityRef,
    tokenCacheRef,
    logger,
  });

  const currentRoom = externalRoom ?? room;

  const replaceIsolatedBaseRoom = useCallback(
    (isolatedRoom: Room) => {
      if (baseRoomRef.current !== isolatedRoom) {
        return baseRoomRef.current;
      }

      const nextRoom = createBaseRoom();
      baseRoomRef.current = nextRoom;
      currentRoomRef.current = nextRoom;
      setRoom(nextRoom);
      logger.info("Installed fresh LiveKit room after local session isolation", {
        previousRoomName: isolatedRoom.name || null,
      });
      return nextRoom;
    },
    [createBaseRoom]
  );

  // These refs are read only by room/timer/unmount event handlers that need the
  // most recently committed session state.
  useEffect(() => {
    baseRoomRef.current = room;
    currentRoomRef.current = currentRoom;
    isConnectedRef.current = isConnected;
    isRecordingRef.current = isRecording;
  }, [currentRoom, isConnected, isRecording]);

  // Track initial render for logging
  useEffect(() => {
    if (tutorPrompt) {
      logger.info("📝 Tutor prompt updated", { tutorId, promptLength: tutorPrompt.length });
    }
  }, [tutorPrompt, tutorId]);

  useLiveKitBrowserLifecycleDiagnostics({
    currentRoomRef,
    isConnectedRef,
    isRecordingRef,
    logger,
  });

  useScreenWakeLock(connectionState === "connected" && isConnected && isRecording);

  const isLessonTransportActive = useCallback(
    () =>
      isConnectedRef.current ||
      isRecordingRef.current ||
      isConnectingRef.current ||
      freshReconnectInProgressRef.current ||
      hasActiveLiveKitConnection(currentRoomRef.current.state),
    []
  );
  useLessonRefreshProtection({
    connectionState,
    isConnected,
    isRecording,
    isTransportActive: isLessonTransportActive,
  });

  const clearSpatiusPrimaryFallbackTimer = useCallback(() => {
    if (spatiusPrimaryFallbackTimerRef.current) {
      clearTimeout(spatiusPrimaryFallbackTimerRef.current);
      spatiusPrimaryFallbackTimerRef.current = null;
    }
  }, []);

  const clearSpatiusPrimaryBaseAudioWatchdogTimer = useCallback(() => {
    if (spatiusPrimaryBaseAudioWatchdogTimerRef.current) {
      clearTimeout(spatiusPrimaryBaseAudioWatchdogTimerRef.current);
      spatiusPrimaryBaseAudioWatchdogTimerRef.current = null;
    }
  }, []);

  const setSpatiusPrimaryAudioFallback = useCallback((active: boolean) => {
    spatiusPrimaryAudioFallbackActiveRef.current = active;
    setSpatiusPrimaryAudioFallbackActive(active);
  }, []);

  const captureLiveKitConnectionEvent = useCallback(
    (eventName: string, error: unknown, extra?: Record<string, unknown>) => {
      const reportedException = captureLiveKitConnectionEventTelemetry({
        eventName,
        posthogClient,
        hasPosthogKey: Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
        error,
        selectedLanguageCode,
        selectedLanguageLevel,
        userType: user ? "authenticated" : "guest",
        sessionMode: lastRequestedModeRef.current,
        details: activeConnectionDetailsRef.current,
        traceId: latencyTraceRef.current?.traceId || null,
        phase: connectionFailurePhaseRef.current,
        spatiusPrimary: shouldUseSpatiusPrimaryRoom,
        selectedVoice,
        avatarId,
        noAvatar,
        extra: {
          attempt_id: activeConnectAttemptIdRef.current || null,
          retry_count: connectRetryCountRef.current,
          active_room_state: currentRoomRef.current.state,
          handled_exception_reported: handledConnectionExceptionReportedRef.current || undefined,
          ...extra,
        },
      });
      if (reportedException) {
        handledConnectionExceptionReportedRef.current = true;
      }
      return reportedException;
    },
    [
      avatarId,
      latencyTraceRef,
      noAvatar,
      posthogClient,
      selectedLanguageCode,
      selectedLanguageLevel,
      selectedVoice,
      shouldUseSpatiusPrimaryRoom,
      user,
    ]
  );

  const resetSpatiusPrimarySession = useCallback(
    (reason: string = "unspecified", options: { preserveAudioFallback?: boolean } = {}) => {
      const viewerRoom = spatiusPrimaryViewerRoomRef.current;
      const participantConnectedHandler = spatiusPrimaryParticipantConnectedHandlerRef.current;
      const trackPublishedHandler = spatiusPrimaryTrackPublishedHandlerRef.current;
      const trackSubscribedHandler = spatiusPrimaryTrackSubscribedHandlerRef.current;
      const pendingConnect = spatiusPrimaryConnectRef.current;
      const previousSessionVersion = spatiusPrimarySessionVersionRef.current;
      const nextSessionVersion = previousSessionVersion + 1;

      logger.info("Resetting Spatius primary session", {
        reason,
        baseRoomName: currentRoomRef.current.name || null,
        baseRoomState: currentRoomRef.current.state,
        viewerRoomName: viewerRoom?.name || null,
        viewerRoomState: viewerRoom?.state || null,
        pendingRoomName: pendingConnect?.details.roomName || null,
        wasConnected: spatiusPrimaryConnectedRef.current,
        fallbackInProgress: spatiusPrimaryFallbackInProgressRef.current,
        previousSessionVersion,
        nextSessionVersion,
        ...getBrowserSessionDiagnostics(),
      });

      if (viewerRoom && participantConnectedHandler) {
        viewerRoom.off(RoomEvent.ParticipantConnected, participantConnectedHandler);
      }

      if (viewerRoom && trackPublishedHandler) {
        viewerRoom.off(RoomEvent.TrackPublished, trackPublishedHandler);
      }

      if (viewerRoom && trackSubscribedHandler) {
        viewerRoom.off(RoomEvent.TrackSubscribed, trackSubscribedHandler);
      }

      if (viewerRoom && hasActiveLiveKitConnection(viewerRoom.state)) {
        const viewerRoomName = viewerRoom.name || null;
        void Promise.resolve(viewerRoom.disconnect()).catch((error) => {
          logger.warn("Failed to disconnect Spatius viewer room during reset", {
            viewerRoomName,
            error: error instanceof Error ? error.message : String(error),
          });
        });
        const cleanupResult = cleanupSpatiusSdkAudioElements();
        if (cleanupResult.removedCount > 0) {
          logger.warn("Removed Spatius shared viewer SDK audio elements", {
            reason: "spatius_primary_viewer_reset",
            viewerRoomName,
            removedCount: cleanupResult.removedCount,
            elementIds: cleanupResult.elementIds,
          });
        }
        logger.info("Spatius viewer room disconnect requested during reset", {
          viewerRoomName,
        });
      }

      spatiusPrimaryViewerRoomRef.current = null;
      spatiusPrimaryParticipantConnectedHandlerRef.current = null;
      spatiusPrimaryTrackPublishedHandlerRef.current = null;
      spatiusPrimaryTrackSubscribedHandlerRef.current = null;
      spatiusPrimarySessionVersionRef.current = nextSessionVersion;
      clearSpatiusPrimaryFallbackTimer();
      clearSpatiusPrimaryBaseAudioWatchdogTimer();
      if (!options.preserveAudioFallback) {
        setSpatiusPrimaryAudioFallback(false);
      }
      spatiusPrimaryConnectedRef.current = false;
      spatiusPrimaryFallbackInProgressRef.current = false;
      spatiusPrimaryConnectRef.current = null;
      setSpatiusPrimarySession(null);
      setExternalRoom(null);
    },
    [
      clearSpatiusPrimaryBaseAudioWatchdogTimer,
      clearSpatiusPrimaryFallbackTimer,
      setSpatiusPrimaryAudioFallback,
    ]
  );

  const logSpatiusPrimaryViewerAudioDiagnostics = useCallback(
    (reason: string, viewerRoom: Room) => {
      const activeAudioPublications = collectActiveRemoteAudioPublications(viewerRoom);

      logger.info("Spatius viewer room audio diagnostics", {
        reason,
        viewerRoomName: viewerRoom.name || null,
        activeAudioCount: activeAudioPublications.length,
        activeAudioPublications: activeAudioPublications.map((publication) => ({
          participantIdentity: publication.participantIdentity,
          trackSid: publication.trackSid,
        })),
        activeSessionGeneration: activeAudioSessionGenerationRef.current,
      });
    },
    []
  );

  const syncSpatiusPrimaryViewerSubscriptions = useCallback(
    (viewerRoom: Room, reason = "spatius_viewer_subscription_sync") => {
      for (const participant of viewerRoom.remoteParticipants.values()) {
        for (const publication of participant.trackPublications.values()) {
          const shouldSubscribe = shouldSubscribeSpatiusViewerPublication({
            participantIdentity: participant.identity,
            publicationKind: publication.kind,
          });

          try {
            publication.setSubscribed(shouldSubscribe);
          } catch (error) {
            logger.warn("Failed to update Spatius shared viewer subscription", {
              participant: participant.identity,
              shouldSubscribe,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      }

      const cleanupResult = silenceSpatiusSdkAudioElementsForLipSync();
      if (cleanupResult.removedCount > 0 || cleanupResult.mutedCount > 0) {
        logger.warn("Silenced Spatius shared viewer SDK audio elements", {
          reason,
          viewerRoomName: viewerRoom.name || null,
          removedCount: cleanupResult.removedCount,
          elementIds: cleanupResult.elementIds,
          mutedCount: cleanupResult.mutedCount,
          mutedElementIds: cleanupResult.mutedElementIds,
        });
      }

      logSpatiusPrimaryViewerAudioDiagnostics(reason, viewerRoom);
    },
    [logSpatiusPrimaryViewerAudioDiagnostics]
  );

  const getSpatiusPrimaryBaseRoomAudioOwner = useCallback((targetRoom: Room) => {
    const avatarAudioPublications: Array<{
      participantIdentity: string | null;
      publication: any;
    }> = [];

    for (const participant of targetRoom.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        if (
          isSpatiusAvatarIdentity(participant.identity) &&
          publication.kind === Track.Kind.Audio
        ) {
          avatarAudioPublications.push({
            participantIdentity: participant.identity || null,
            publication,
          });
        }
      }
    }

    return avatarAudioPublications[avatarAudioPublications.length - 1] ?? null;
  }, []);

  const syncSpatiusPrimaryBaseRoomAudioSubscriptions = useCallback(
    (targetRoom: Room) => {
      if (spatiusPrimaryAudioFallbackActiveRef.current) {
        for (const participant of targetRoom.remoteParticipants.values()) {
          for (const publication of participant.trackPublications.values()) {
            if (publication.kind !== Track.Kind.Audio) {
              continue;
            }

            try {
              publication.setSubscribed(true);
            } catch (error) {
              logger.warn("Failed to restore normal base room audio subscription", {
                participant: participant.identity,
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }
        }
        return;
      }

      const audioOwner = getSpatiusPrimaryBaseRoomAudioOwner(targetRoom);

      for (const participant of targetRoom.remoteParticipants.values()) {
        for (const publication of participant.trackPublications.values()) {
          if (publication.kind !== Track.Kind.Audio) {
            continue;
          }

          const shouldSubscribe = Boolean(audioOwner && publication === audioOwner.publication);

          try {
            publication.setSubscribed(shouldSubscribe);
          } catch (error) {
            logger.warn("Failed to update Spatius base room audio subscription", {
              participant: participant.identity,
              shouldSubscribe,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      }
    },
    [getSpatiusPrimaryBaseRoomAudioOwner]
  );

  const ensureSpatiusPrimaryBaseRoomAudioPlayback = useCallback(
    async (
      targetRoom: Room,
      reason: string,
      { startPlayback = false }: { startPlayback?: boolean } = {}
    ) => {
      if (spatiusPrimaryAudioFallbackActiveRef.current) {
        let playbackStarted: boolean | null = null;
        if (startPlayback && !targetRoom.canPlaybackAudio) {
          const playbackResult = await startRoomAudioPlayback(targetRoom);
          playbackStarted = playbackResult.ok;
          if (!playbackResult.ok) {
            logger.warn("Failed to start normal base room audio playback after Spatius fallback", {
              reason,
              roomName: targetRoom.name || null,
              error: playbackResult.error.message,
            });
          }
        }

        return {
          avatarAudioPublicationCount: 0,
          resubscribeRequestCount: 0,
          unsubscribeRequestCount: 0,
          playbackStarted,
          fallbackActive: true,
        };
      }

      let avatarAudioPublicationCount = 0;
      let resubscribeRequestCount = 0;
      let unsubscribeRequestCount = 0;
      const audioOwner = getSpatiusPrimaryBaseRoomAudioOwner(targetRoom);
      const avatarAudioPublications: Array<{
        participantIdentity: string | null;
        trackSid: string | null;
        subscribed: boolean;
        hasTrack: boolean;
        muted: boolean;
      }> = [];

      for (const participant of targetRoom.remoteParticipants.values()) {
        const isSpatiusAvatarParticipant = isSpatiusAvatarIdentity(participant.identity);

        for (const publication of participant.trackPublications.values()) {
          if (publication.kind !== Track.Kind.Audio) {
            continue;
          }

          const publicationState = publication as typeof publication & {
            isSubscribed?: boolean;
            subscribed?: boolean;
            track?: unknown;
            muted?: boolean;
            isMuted?: boolean;
            sid?: string;
          };
          const isSubscribed = Boolean(
            publicationState.isSubscribed ?? publicationState.subscribed
          );
          const hasTrack = Boolean(publicationState.track);
          const muted = Boolean(publicationState.isMuted ?? publicationState.muted);
          const trackSid = publicationState.trackSid || publicationState.sid || null;
          const isAudioOwner = Boolean(audioOwner && publication === audioOwner.publication);

          if (isSpatiusAvatarParticipant) {
            avatarAudioPublicationCount += 1;
            avatarAudioPublications.push({
              participantIdentity: participant.identity || null,
              trackSid,
              subscribed: isSubscribed,
              hasTrack,
              muted,
            });
          }

          if (isAudioOwner && (!isSubscribed || !hasTrack)) {
            try {
              publication.setSubscribed(true);
              resubscribeRequestCount += 1;
            } catch (error) {
              logger.warn("Failed to ensure Spatius avatar audio subscription", {
                reason,
                participantIdentity: participant.identity || null,
                trackSid,
                subscribed: isSubscribed,
                hasTrack,
                error: error instanceof Error ? error.message : String(error),
              });
            }
          } else if (!isAudioOwner && isSubscribed) {
            try {
              publication.setSubscribed(false);
              unsubscribeRequestCount += 1;
            } catch (error) {
              logger.warn("Failed to suppress duplicate Spatius avatar audio subscription", {
                reason,
                participantIdentity: participant.identity || null,
                trackSid,
                isSpatiusAvatarParticipant,
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }
        }
      }

      let playbackStarted: boolean | null = null;
      if (startPlayback && !targetRoom.canPlaybackAudio) {
        const playbackResult = await startRoomAudioPlayback(targetRoom);
        playbackStarted = playbackResult.ok;
        if (!playbackResult.ok) {
          logger.warn("Failed to start Spatius base room audio playback", {
            reason,
            roomName: targetRoom.name || null,
            error: playbackResult.error.message,
          });
        }
      }

      if (avatarAudioPublicationCount === 0) {
        logger.warn("No Spatius avatar audio publication found in base room", {
          reason,
          roomName: targetRoom.name || null,
          roomState: targetRoom.state,
          canPlaybackAudio: targetRoom.canPlaybackAudio,
        });
      } else if (
        resubscribeRequestCount > 0 ||
        unsubscribeRequestCount > 0 ||
        playbackStarted !== null
      ) {
        logger.info("Ensured Spatius avatar audio playback", {
          reason,
          roomName: targetRoom.name || null,
          roomState: targetRoom.state,
          canPlaybackAudio: targetRoom.canPlaybackAudio,
          playbackStarted,
          avatarAudioPublicationCount,
          resubscribeRequestCount,
          unsubscribeRequestCount,
          avatarAudioPublications,
        });
      }

      return {
        avatarAudioPublicationCount,
        resubscribeRequestCount,
        unsubscribeRequestCount,
        playbackStarted,
      };
    },
    [getSpatiusPrimaryBaseRoomAudioOwner]
  );

  const logDuplicateAudioPublications = useCallback(
    (reason: string, targetRoom: Room = currentRoomRef.current) => {
      const result = inspectDuplicateRemoteAudioPublications(targetRoom, {
        preferSpatiusAvatarAudio: shouldUseSpatiusPrimaryRoom && !spatiusPrimaryAudioFallbackActive,
      });
      const generation = activeAudioSessionGenerationRef.current;
      const rendererDiagnostics = getMountedRoomAudioRendererDiagnostics();
      const activeRoomName = targetRoom.name || null;
      const duplicatePublications = result.duplicates.map((publication) => ({
        participantIdentity: publication.participantIdentity,
        trackSid: publication.trackSid,
      }));
      const activePublications = collectActiveRemoteAudioPublications(targetRoom).map(
        (publication) => ({
          participantIdentity: publication.participantIdentity,
          trackSid: publication.trackSid,
        })
      );

      if (result.activeCount > 1) {
        logger.warn("Duplicate remote audio publications observed", {
          reason,
          activeCount: result.activeCount,
          activeRoomName,
          activeSessionGeneration: generation,
          keptParticipantIdentity: result.kept?.participantIdentity || null,
          keptTrackSid: result.kept?.trackSid || null,
          duplicatePublications,
          mountedAudioRendererCount: rendererDiagnostics.mountedAudioRendererCount,
          mountedAudioRendererSurfaces: rendererDiagnostics.mountedAudioRendererSurfaces,
        });
      }

      const hasRendererDuplicationRisk =
        rendererDiagnostics.activeMountedAudioRendererCount > 1 && result.activeCount >= 1;
      const shouldCaptureDiagnostics =
        result.activeCount > 1 ||
        hasRendererDuplicationRisk ||
        reason !== "active_session_interval" ||
        Date.now() - lastAudioDiagnosticsPostHogAtRef.current > 30_000;

      if (posthogClient && process.env.NEXT_PUBLIC_POSTHOG_KEY && shouldCaptureDiagnostics) {
        lastAudioDiagnosticsPostHogAtRef.current = Date.now();
        posthogClient.capture("livekit_audio_playback_diagnostics", {
          reason,
          room_name: activeRoomName,
          active_remote_audio_count: result.activeCount,
          duplicate_remote_audio_count: result.duplicates.length,
          duplicate_remote_audio_publications_observed: result.activeCount > 1,
          possible_renderer_duplication: hasRendererDuplicationRisk,
          active_session_generation: generation,
          spatius_primary: shouldUseSpatiusPrimaryRoom,
          spatius_audio_fallback_active: spatiusPrimaryAudioFallbackActive,
          kept_participant_identity: result.kept?.participantIdentity || null,
          kept_track_sid: result.kept?.trackSid || null,
          remote_audio_publications: activePublications.slice(0, 10),
          duplicate_remote_audio_publications: duplicatePublications.slice(0, 10),
          mounted_audio_renderer_count: rendererDiagnostics.mountedAudioRendererCount,
          active_mounted_audio_renderer_count: rendererDiagnostics.activeMountedAudioRendererCount,
          mounted_audio_renderer_surfaces: rendererDiagnostics.mountedAudioRendererSurfaces.slice(
            0,
            10
          ),
        });
      }

      return result;
    },
    [posthogClient, shouldUseSpatiusPrimaryRoom, spatiusPrimaryAudioFallbackActive]
  );

  const handleSuccessfulSessionConnect = useCallback(
    (mode: SessionMode) => {
      markLatencyPhase("session_connected", {
        mode,
        roomConnectionState: currentRoomRef.current.state,
      });
      freezeConnectionElapsedTimer();
      isConnectedRef.current = true;
      isRecordingRef.current = true;
      setIsConnected(true);
      setIsRecording(true);
      setConnectionState("connected");
      setAgentState("connecting");
      setConnectionError(null);
      connectionFailurePhaseRef.current = "idle";
      connectionFailureStickyRef.current = false;
      connectRetryCountRef.current = 0;
      websocket1006RetryCountRef.current = 0;
      auth401RetryPhaseRef.current = 0;
      activeAudioSessionGenerationRef.current += 1;
      setIsRetryingAsGuest(false);
      setInteractionMode(
        mode === "conversation" ||
          mode === "stealth" ||
          mode === "zen" ||
          mode === "journey" ||
          mode === "maths"
          ? "voice"
          : "text"
      );

      const selectedLanguageName = getLanguageNameByCode(selectedLanguageCode);
      const guestSecondsUsedToday = getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, logger);
      if (mode !== "maths")
        recordSuccessfulSessionConnectAnalytics({
          mode,
          userType: user ? "authenticated" : "guest",
          selectedLanguageCode,
          selectedLanguageName,
          selectedLanguageLevel,
          selectedVoice,
          avatarId,
          noAvatar,
          instructionLanguageCode: fromLanguageCode || null,
          instructionLanguageName: fromLanguageName || null,
          roomName: currentRoomRef.current.name || null,
          traceId: latencyTraceRef.current?.traceId || null,
          participantToken: activeConnectionDetailsRef.current?.participantToken || null,
          spatiusPrimary: shouldUseSpatiusPrimaryRoom,
          spatiusAudioFallbackActive: spatiusPrimaryAudioFallbackActive,
          tokenSource: activeConnectionDetailsRef.current?.tokenSource || null,
          whiteboardEnabled: tutorWhiteboardEnabled,
          analyticsSurface,
          experimentProperties: guestSpatiusAvatarExperimentPropertiesRef.current || undefined,
          webappSignupBeforeDemoExperimentProperties:
            webappSignupBeforeDemoExperimentEligible &&
            getWebappSignupBeforeDemoExperimentProperties
              ? getWebappSignupBeforeDemoExperimentProperties()
              : undefined,
          guestTotalUsageSeconds: getGuestTotalUsageSeconds(),
          maxGuestSeconds: MAX_GUEST_SECONDS,
          authenticatedUserId: user?.id || null,
          authenticatedUserCreatedToday: Boolean(user && wasUserCreatedToday(user.created_at)),
          guestSecondsUsedToday,
          hasTrackedGuestAuthenticatedSessionStarted:
            hasTrackedGuestAuthenticatedSessionStartedRef.current,
          markGuestAuthenticatedSessionStarted: () => {
            hasTrackedGuestAuthenticatedSessionStartedRef.current = true;
          },
          posthogClient,
          hasPosthogKey: Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
          logger,
        });
      logger.info("LiveKit audio ownership generation activated", {
        generation: activeAudioSessionGenerationRef.current,
        roomName: currentRoomRef.current.name || null,
        spatiusReal: shouldUseSpatiusPrimaryRoom,
      });
    },
    [
      analyticsSurface,
      avatarId,
      freezeConnectionElapsedTimer,
      latencyTraceRef,
      markLatencyPhase,
      noAvatar,
      posthogClient,
      fromLanguageCode,
      fromLanguageName,
      getWebappSignupBeforeDemoExperimentProperties,
      selectedLanguageCode,
      selectedLanguageLevel,
      selectedVoice,
      setInteractionMode,
      shouldUseSpatiusPrimaryRoom,
      spatiusPrimaryAudioFallbackActive,
      tutorWhiteboardEnabled,
      user,
      webappSignupBeforeDemoExperimentEligible,
      MAX_GUEST_SECONDS,
    ]
  );

  const fetchSpatiusPrimarySession = useCallback(
    async (
      roomName: string,
      voice: string,
      participantToken: string,
      requestedAvatarId?: string
    ): Promise<SpatiusBootstrapResponse> => {
      const params = new URLSearchParams({ mode: "viewer", roomName });
      if (voice) {
        params.set("voice", voice);
      }
      if (requestedAvatarId) {
        params.set("avatarId", requestedAvatarId);
      }
      if (latencyTraceRef.current) {
        params.set("traceId", latencyTraceRef.current.traceId);
        if (typeof latencyTraceRef.current.clientStartMs === "number") {
          params.set("clientStartMs", String(latencyTraceRef.current.clientStartMs));
        }
      }

      const response = await fetch(`/api/spatius/rtc-credentials?${params.toString()}`, {
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${participantToken}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Spatius bootstrap failed (${response.status}): ${text}`);
      }

      return (await response.json()) as SpatiusBootstrapResponse;
    },
    [latencyTraceRef]
  );

  const sendAvatarAudioFallbackControlMessage = useCallback(
    async (reason: SpatiusPrimaryFallbackReason): Promise<boolean> => {
      const activeRoom = currentRoomRef.current;
      if (!hasActiveLiveKitConnection(activeRoom.state)) {
        return false;
      }

      try {
        const payload = {
          type: "avatar_audio_fallback",
          source: "spatius_primary_base_audio_watchdog",
          reason,
          sentAt: new Date().toISOString(),
          traceId: latencyTraceRef.current?.traceId || null,
        };
        await activeRoom.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(payload)),
          {
            reliable: true,
            topic: "session-control",
          }
        );
        logger.info("Sent Spatius avatar audio fallback control message", {
          reason,
          roomName: activeRoom.name || null,
        });
        return true;
      } catch (error) {
        logger.warn("Failed to send Spatius avatar audio fallback control message", {
          reason,
          roomName: activeRoom.name || null,
          error: error instanceof Error ? error.message : String(error),
        });
        return false;
      }
    },
    [latencyTraceRef]
  );

  const activateSpatiusPrimaryAudioFallback = useCallback(
    async (reason: SpatiusPrimaryFallbackReason) => {
      if (!shouldUseSpatiusPrimaryRoom || spatiusPrimaryAudioFallbackActiveRef.current) {
        return;
      }

      markLatencyPhase("spatius_primary_audio_fallback", {
        reason,
        roomName: currentRoomRef.current.name || null,
      });
      setSpatiusPrimaryAudioFallback(true);
      clearSpatiusPrimaryBaseAudioWatchdogTimer();
      resetSpatiusPrimarySession(`spatius_audio_fallback:${reason}`, {
        preserveAudioFallback: true,
      });
      await sendAvatarAudioFallbackControlMessage(reason);

      try {
        currentRoomRef.current.localParticipant.setAttributes({ avatar_ready: "true" });
      } catch (attrError) {
        logger.warn("Failed to set avatar_ready attribute on Spatius audio fallback", {
          reason,
          error: attrError instanceof Error ? attrError.message : String(attrError),
        });
      }

      syncSpatiusPrimaryBaseRoomAudioSubscriptions(currentRoomRef.current);
      void ensureSpatiusPrimaryBaseRoomAudioPlayback(
        currentRoomRef.current,
        `spatius_audio_fallback:${reason}`,
        {
          startPlayback: true,
        }
      );

      if (posthogClient && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        posthogClient.capture("spatius_primary_audio_fallback_activated", {
          reason,
          room_name: currentRoomRef.current.name || null,
          trace_id: latencyTraceRef.current?.traceId || null,
          ...(guestSpatiusAvatarExperimentPropertiesRef.current || {}),
        });
      }

      logger.warn("Spatius primary audio fallback activated", {
        reason,
        roomName: currentRoomRef.current.name || null,
      });
    },
    [
      clearSpatiusPrimaryBaseAudioWatchdogTimer,
      ensureSpatiusPrimaryBaseRoomAudioPlayback,
      latencyTraceRef,
      markLatencyPhase,
      posthogClient,
      resetSpatiusPrimarySession,
      sendAvatarAudioFallbackControlMessage,
      setSpatiusPrimaryAudioFallback,
      shouldUseSpatiusPrimaryRoom,
      syncSpatiusPrimaryBaseRoomAudioSubscriptions,
    ]
  );

  const fallbackToAudioOnly = useCallback(
    async (reason: Extract<SpatiusPrimaryFallbackReason, "error" | "timeout">) => {
      const pending = spatiusPrimaryConnectRef.current;
      if (!pending || spatiusPrimaryFallbackInProgressRef.current) {
        return;
      }

      markLatencyPhase("spatius_primary_fallback", {
        reason,
      });

      const keepPendingViewerSession = shouldKeepSpatiusPrimarySessionOnFallback(reason);
      spatiusPrimaryFallbackInProgressRef.current = true;
      clearSpatiusPrimaryFallbackTimer();
      spatiusPrimaryConnectedRef.current = false;
      if (!keepPendingViewerSession) {
        setSpatiusPrimarySession(null);
        setExternalRoom(null);
        spatiusPrimaryConnectRef.current = null;
      }
      spatiusPrimaryFallbackInProgressRef.current = false;
      // Signal agent that avatar won't be available — safe to send greeting now
      try {
        currentRoomRef.current.localParticipant.setAttributes({ avatar_ready: "true" });
      } catch (attrError) {
        logger.warn("Failed to set avatar_ready attribute on fallback", {
          error: attrError instanceof Error ? attrError.message : String(attrError),
        });
      }
      logger.warn("Spatius viewer unavailable; continuing without avatar", {
        reason,
        roomName: pending.details.roomName,
        keptPendingViewerSession: keepPendingViewerSession,
      });
    },
    [clearSpatiusPrimaryFallbackTimer, markLatencyPhase]
  );

  const beginSpatiusPrimarySession = useCallback(
    async (details: SessionConnectionDetails, mode: SessionMode, version: number) => {
      markLatencyPhase("spatius_primary_bootstrap_started", {
        roomName: details.roomName,
        mode,
      });
      setSpatiusPrimaryAudioFallback(false);
      spatiusPrimaryConnectRef.current = { details, mode };
      spatiusPrimaryConnectedRef.current = false;
      spatiusPrimaryFallbackInProgressRef.current = false;
      clearSpatiusPrimaryFallbackTimer();
      spatiusPrimaryFallbackTimerRef.current = setTimeout(() => {
        void fallbackToAudioOnly("timeout");
      }, SPATIUS_PRIMARY_FALLBACK_DELAY_MS);
      const spatiusBootstrap = await fetchSpatiusPrimarySession(
        details.roomName,
        selectedVoice,
        details.participantToken,
        avatarId
      );
      if (spatiusPrimarySessionVersionRef.current !== version) {
        return;
      }
      markLatencyPhase("spatius_primary_bootstrap_completed", {
        roomName: details.roomName,
        mode,
      });

      if (!spatiusBootstrap.participantToken) {
        throw new Error("Spatius viewer bootstrap did not return a participant token.");
      }

      spatiusPrimaryConnectedRef.current = false;
      spatiusPrimaryFallbackInProgressRef.current = false;
      setExternalRoom(null);
      setSpatiusPrimarySession({
        connection: {
          roomName: spatiusBootstrap.roomName || details.roomName,
          token: spatiusBootstrap.participantToken,
          url: spatiusBootstrap.livekitUrl || details.serverUrl,
        },
        mode,
        spatius: spatiusBootstrap.spatius,
      });

      clearSpatiusPrimaryFallbackTimer();
      spatiusPrimaryFallbackTimerRef.current = setTimeout(() => {
        void fallbackToAudioOnly("timeout");
      }, SPATIUS_PRIMARY_FALLBACK_DELAY_MS);
    },
    [
      clearSpatiusPrimaryFallbackTimer,
      fallbackToAudioOnly,
      fetchSpatiusPrimarySession,
      markLatencyPhase,
      setSpatiusPrimaryAudioFallback,
      avatarId,
      selectedVoice,
    ]
  );

  const scheduleSpatiusPrimaryBaseAudioWatchdog = useCallback(
    (reason: string) => {
      clearSpatiusPrimaryBaseAudioWatchdogTimer();

      if (!shouldUseSpatiusPrimaryRoom || spatiusPrimaryAudioFallbackActiveRef.current) {
        return;
      }

      const sessionVersion = spatiusPrimarySessionVersionRef.current;
      spatiusPrimaryBaseAudioWatchdogTimerRef.current = setTimeout(() => {
        void (async () => {
          if (
            sessionVersion !== spatiusPrimarySessionVersionRef.current ||
            spatiusPrimaryAudioFallbackActiveRef.current
          ) {
            return;
          }

          const activeRoom = currentRoomRef.current;
          if (!hasActiveLiveKitConnection(activeRoom.state)) {
            return;
          }

          const result = await ensureSpatiusPrimaryBaseRoomAudioPlayback(
            activeRoom,
            `spatius_base_audio_watchdog:${reason}`,
            {
              startPlayback: true,
            }
          );

          if (result.avatarAudioPublicationCount > 0) {
            logger.info("Spatius base room audio watchdog passed", {
              reason,
              roomName: activeRoom.name || null,
              avatarAudioPublicationCount: result.avatarAudioPublicationCount,
            });
            return;
          }

          const activePublications = collectActiveRemoteAudioPublications(activeRoom).map(
            (publication) => ({
              participantIdentity: publication.participantIdentity,
              trackSid: publication.trackSid,
            })
          );
          const rendererDiagnostics = getMountedRoomAudioRendererDiagnostics();

          logger.warn("Spatius base room audio watchdog failed; activating audio fallback", {
            reason,
            roomName: activeRoom.name || null,
            roomState: activeRoom.state,
            activeRemoteAudioCount: activePublications.length,
            activeRemoteAudioPublications: activePublications.slice(0, 10),
            mountedAudioRendererCount: rendererDiagnostics.mountedAudioRendererCount,
            activeMountedAudioRendererCount: rendererDiagnostics.activeMountedAudioRendererCount,
          });

          if (posthogClient && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
            posthogClient.capture("spatius_primary_base_audio_watchdog_failed", {
              reason,
              room_name: activeRoom.name || null,
              trace_id: latencyTraceRef.current?.traceId || null,
              active_remote_audio_count: activePublications.length,
              remote_audio_publications: activePublications.slice(0, 10),
              mounted_audio_renderer_count: rendererDiagnostics.mountedAudioRendererCount,
              active_mounted_audio_renderer_count:
                rendererDiagnostics.activeMountedAudioRendererCount,
            });
          }

          await activateSpatiusPrimaryAudioFallback("missing_base_audio");
        })();
      }, SPATIUS_PRIMARY_BASE_AUDIO_WATCHDOG_DELAY_MS);
    },
    [
      activateSpatiusPrimaryAudioFallback,
      clearSpatiusPrimaryBaseAudioWatchdogTimer,
      ensureSpatiusPrimaryBaseRoomAudioPlayback,
      latencyTraceRef,
      posthogClient,
      shouldUseSpatiusPrimaryRoom,
    ]
  );

  const getConnectCancelOrigin = useCallback((attemptId: number): LiveKitConnectCancelOrigin => {
    const cancellation = connectCancelOriginRef.current;
    return cancellation?.attemptId === attemptId ? cancellation.origin : "none";
  }, []);

  const markConnectCancelOrigin = useCallback(
    (attemptId: number, origin: Exclude<LiveKitConnectCancelOrigin, "none">) => {
      connectCancelOriginRef.current = {
        attemptId,
        origin,
      };
    },
    []
  );

  const connectWithToken = useCallback(
    async (
      serverUrl: string,
      token: string,
      mode: SessionMode,
      resetSpatiusPrimaryBeforeConnect: boolean,
      holdGreetingUntilUserReady: boolean,
      attemptId: number
    ) => {
      const targetRoom = baseRoomRef.current;
      currentRoomRef.current = targetRoom;
      if (resetSpatiusPrimaryBeforeConnect) {
        resetSpatiusPrimarySession("base_room_pre_connect");
      }
      connectionFailurePhaseRef.current = "livekit_room_connect";
      markLatencyPhase("livekit_room_connect_started", { mode });
      try {
        rememberLiveKitRoomParticipantToken(targetRoom, token);
        await targetRoom.connect(serverUrl, token, { autoSubscribe: true });
      } catch (connectError) {
        const cancelOrigin = getConnectCancelOrigin(attemptId);
        captureLiveKitConnectionEvent("livekit_room_connect_failed", connectError, {
          mode,
          room_name: activeConnectionDetailsRef.current?.roomName || null,
          server_url_host: getServerUrlHost(serverUrl),
          connect_cancel_origin: cancelOrigin,
          timeout_guard_disconnect: cancelOrigin === "timeout_guard" || undefined,
        });
        throw connectError;
      }

      assertCurrentLiveKitConnectionAttempt(attemptId, activeConnectAttemptIdRef.current);

      if (shouldUseSpatiusPrimaryRoom) {
        syncSpatiusPrimaryBaseRoomAudioSubscriptions(targetRoom);
      }
      logDuplicateAudioPublications("livekit_room_connect_completed", targetRoom);
      markLatencyPhase("livekit_room_connect_completed", { mode });

      const initialAttributes: Record<string, string> = {};
      if (holdGreetingUntilUserReady) {
        initialAttributes.user_ready_for_greeting = "false";
      }
      if (shouldUseSpatiusPrimaryRoom) {
        initialAttributes.avatar_ready = "false";
      }
      if (Object.keys(initialAttributes).length > 0) {
        try {
          targetRoom.localParticipant.setAttributes(initialAttributes);
        } catch (attrError) {
          logger.warn("Failed to set initial participant attributes", {
            error: attrError instanceof Error ? attrError.message : String(attrError),
            attributes: initialAttributes,
          });
        }
      }

      logger.info("Successfully connected to LiveKit room.");
      if (
        mode === "conversation" ||
        mode === "stealth" ||
        mode === "zen" ||
        mode === "journey" ||
        mode === "maths"
      ) {
        try {
          connectionFailurePhaseRef.current = "microphone_enable";
          markLatencyPhase("microphone_enable_started", { mode });
          await targetRoom.localParticipant.setMicrophoneEnabled(true);
          markLatencyPhase("microphone_enable_completed", { mode });
        } catch (micError) {
          markLatencyPhase("microphone_enable_failed", {
            error: micError instanceof Error ? micError.message : String(micError),
          });
          logger.error("Failed to enable microphone:", micError);
          const permissionLikeError = isMicrophonePermissionError(micError);
          captureLiveKitConnectionEvent("microphone_enable_failed", micError, {
            mode,
            permission_like_error: permissionLikeError,
          });
          setAudioTrackError(
            permissionLikeError
              ? AUDIO_MICROPHONE_PERMISSION_MESSAGE
              : "We couldn't start your microphone. Check your device settings, then try again."
          );
          throw micError;
        }
      } else {
        await targetRoom.localParticipant.setMicrophoneEnabled(false);
      }

      if (holdGreetingUntilUserReady) {
        try {
          targetRoom.localParticipant.setAttributes({ user_ready_for_greeting: "true" });
        } catch (attrError) {
          logger.warn("Failed to set user_ready_for_greeting=true attribute", {
            error: attrError instanceof Error ? attrError.message : String(attrError),
          });
        }
      }
      assertCurrentLiveKitConnectionAttempt(attemptId, activeConnectAttemptIdRef.current);
      handleSuccessfulSessionConnect(mode);
    },
    [
      captureLiveKitConnectionEvent,
      getConnectCancelOrigin,
      handleSuccessfulSessionConnect,
      logDuplicateAudioPublications,
      markLatencyPhase,
      resetSpatiusPrimarySession,
      shouldUseSpatiusPrimaryRoom,
      syncSpatiusPrimaryBaseRoomAudioSubscriptions,
    ]
  );

  const startManagedSession = useCallback(
    async (details: SessionConnectionDetails, mode: SessionMode, attemptId: number) => {
      activeConnectionDetailsRef.current = details;
      rememberLiveKitRoomSessionProof(
        baseRoomRef.current,
        details.shutdownCapability && details.participantName
          ? {
              roomName: details.roomName,
              participantIdentity: details.participantName,
              capability: details.shutdownCapability,
            }
          : null
      );
      connectionFailurePhaseRef.current = "managed_session";
      const resetSpatiusPrimaryBeforeConnect = shouldResetSpatiusPrimarySessionBeforeConnect(
        shouldUseSpatiusPrimaryRoom
      );

      if (!resetSpatiusPrimaryBeforeConnect) {
        resetSpatiusPrimarySession("managed_session_start");
      }

      if (shouldUseSpatiusPrimaryRoom) {
        const sessionVersion = spatiusPrimarySessionVersionRef.current;
        void beginSpatiusPrimarySession(details, mode, sessionVersion).catch((error) => {
          const failurePolicy = resolveSpatiusViewerBootstrapFailure(
            sessionVersion,
            spatiusPrimarySessionVersionRef.current
          );
          if (failurePolicy === "ignore_stale") {
            logger.debug("Ignoring stale Spatius viewer bootstrap failure", {
              error: error instanceof Error ? error.message : String(error),
              roomName: details.roomName,
              sessionVersion,
              currentSessionVersion: spatiusPrimarySessionVersionRef.current,
            });
            return;
          }
          connectionFailurePhaseRef.current = "spatius_primary_bootstrap";
          captureLiveKitConnectionEvent("spatius_viewer_connect_failed", error, {
            room_name: details.roomName,
            token_source: details.tokenSource || null,
          });
          logger.warn("Failed to bootstrap Spatius viewer; continuing with base-room audio", {
            error: error instanceof Error ? error.message : String(error),
            roomName: details.roomName,
          });
          void fallbackToAudioOnly("error");
        });
      }

      try {
        await connectWithToken(
          details.serverUrl,
          details.participantToken,
          mode,
          resetSpatiusPrimaryBeforeConnect,
          details.tokenSource === "prewarm",
          attemptId
        );
      } catch (error) {
        if (shouldUseSpatiusPrimaryRoom && !(error instanceof StaleLiveKitConnectionAttemptError)) {
          resetSpatiusPrimarySession("base_room_connect_failed");
        }
        throw error;
      }
    },
    [
      beginSpatiusPrimarySession,
      captureLiveKitConnectionEvent,
      connectWithToken,
      fallbackToAudioOnly,
      resetSpatiusPrimarySession,
      shouldUseSpatiusPrimaryRoom,
    ]
  );

  const scheduleConnectRetry = useCallback(
    (
      attemptId: number,
      reason: string,
      message: string,
      issuanceStartAttemptId: string,
      delayMs = 500
    ): boolean => {
      if (attemptId !== activeConnectAttemptIdRef.current) {
        logger.info("Skipping LiveKit connect retry for stale attempt", {
          attemptId,
          activeAttemptId: activeConnectAttemptIdRef.current,
          reason,
        });
        return false;
      }

      const attempts = connectRetryCountRef.current;
      if (attempts >= MAX_CONNECT_AUTO_RETRIES) {
        return false;
      }

      connectRetryCountRef.current = attempts + 1;
      setConnectionError(message);
      setConnectionState("connecting");
      logger.info("Scheduling LiveKit connect retry", {
        attemptId,
        retryAttempt: connectRetryCountRef.current,
        maxRetries: MAX_CONNECT_AUTO_RETRIES,
        reason,
        roomName:
          activeConnectionDetailsRef.current?.roomName || currentRoomRef.current.name || null,
        roomState: currentRoomRef.current.state,
        freshConnectionDetails: true,
      });

      const connectionDetailsToRelease = activeConnectionDetailsRef.current;

      setTimeout(() => {
        void (async () => {
          if (attemptId !== activeConnectAttemptIdRef.current) {
            logger.info("Skipping delayed LiveKit connect retry for stale attempt", {
              attemptId,
              activeAttemptId: activeConnectAttemptIdRef.current,
              reason,
            });
            return;
          }

          const hasConnectionDetails = Boolean(
            connectionDetailsToRelease?.roomName && connectionDetailsToRelease.participantName
          );
          let serverShutdownSucceeded = false;
          if (connectionDetailsToRelease?.roomName && connectionDetailsToRelease.participantName) {
            const recoveryToken = connectionDetailsToRelease.metered
              ? (await supabase.auth.getSession()).data.session?.access_token
              : undefined;
            serverShutdownSucceeded = await requestServerSessionShutdownForConnectionDetails({
              roomName: connectionDetailsToRelease.roomName,
              participantIdentity: connectionDetailsToRelease.participantName,
              source: `connect_retry:${reason}`,
              shutdownCapability: connectionDetailsToRelease.shutdownCapability,
              timeoutMs: SERVER_SESSION_SHUTDOWN_TIMEOUT_MS,
              logger,
              recover: connectionDetailsToRelease.metered,
              accessToken: recoveryToken,
            });
          }

          if (attemptId !== activeConnectAttemptIdRef.current) {
            return;
          }

          const issuanceRetryDecision = decideVoiceSessionIssuanceRetry({
            startAttemptId: issuanceStartAttemptId,
            hasConnectionDetails,
            serverShutdownSucceeded,
          });
          if (issuanceRetryDecision.action === "fail") {
            connectionFailureStickyRef.current = true;
            setConnectionError(
              "The previous speaking session could not be released. Please try again."
            );
            setConnectionState("failed");
            resetConnectionElapsedTimer();
            logger.warn("Blocked replacement session issuance after unconfirmed shutdown", {
              attemptId,
              reason,
              roomName: connectionDetailsToRelease?.roomName || null,
            });
            return;
          }

          retryIssuanceStartAttemptIdRef.current = issuanceRetryDecision.startAttemptId;
          if (!issuanceRetryDecision.startAttemptId) unresolvedStartRef.current = null;

          if (activeConnectionDetailsRef.current === connectionDetailsToRelease) {
            activeConnectionDetailsRef.current = null;
          }
          void connectToLiveKitCommandRef.current(lastRequestedModeRef.current);
        })();
      }, delayMs);

      return true;
    },
    [resetConnectionElapsedTimer]
  );

  // Connect to LiveKit room
  const connectToLiveKitImpl = useCallback(
    async (requestedSessionMode: SessionMode): Promise<boolean> => {
      const effectiveSessionMode = normalizeSessionMode(requestedSessionMode);
      let supportedSessionMode: ReturnType<typeof getSupportedSessionMode>;
      try {
        supportedSessionMode = getSupportedSessionMode(effectiveSessionMode);
      } catch (error) {
        if (error instanceof UnsupportedSessionModeError) {
          const message = error.message;
          logger.warn("Blocked unsupported session mode request", {
            sessionMode: effectiveSessionMode,
            mathsDemoId,
            mathsFixtureRevision,
          });
          setConnectionError(getUserFacingConnectionFailureMessage(message));
          setConnectionState("failed");
          connectionFailureStickyRef.current = true;
          markLatencyPhase("unsupported_session_mode", {
            sessionMode: effectiveSessionMode,
          });
          return false;
        }

        throw error;
      }

      if (!latencyTraceRef.current) {
        startLatencyTrace(supportedSessionMode);
      }
      markLatencyPhase("connect_invoked", {
        mode: supportedSessionMode,
      });

      // Prevent multiple connection attempts
      if (isConnectingRef.current) {
        logger.info("Connection already in progress, ignoring duplicate request", {
          activeAttemptId: activeConnectAttemptIdRef.current,
          skippedAttemptId: connectAttemptSeqRef.current + 1,
        });
        markLatencyPhase("connect_skipped_duplicate");
        return false;
      }

      const pendingLocalIsolation = activeLocalIsolationPromiseRef.current;
      if (pendingLocalIsolation) {
        isConnectingRef.current = true;
        setConnectionError(null);
        setConnectionState("connecting");
        logger.info("Waiting for previous LiveKit room local isolation before starting", {
          previousRoomName: currentRoomRef.current.name || null,
        });
        try {
          await pendingLocalIsolation;
        } finally {
          isConnectingRef.current = false;
        }
      }

      const attemptId = connectAttemptSeqRef.current + 1;

      // The synchronous scope excludes asynchronously loaded memories/context:
      // Retry keeps the original body, while explicit setup/auth changes cannot
      // silently reuse it. No network work is added to a fresh Start.
      const recoveryScope = getRecoveryScopeRef.current(supportedSessionMode);
      let unresolvedStart = unresolvedStartRef.current;
      if (unresolvedStart && unresolvedStart.scope !== recoveryScope) {
        setConnectionState("failed");
        setConnectionError(
          "The previous speaking start has not finished. Go back before changing setup or account."
        );
        return false;
      }

      const failedDetails =
        connectionFailureStickyRef.current || activeConnectionDetailsRef.current?.requiresRecovery
          ? activeConnectionDetailsRef.current
          : null;
      if (
        (failedDetails?.metered || failedDetails?.requiresRecovery) &&
        failedDetails.roomName &&
        failedDetails.participantName
      ) {
        if (failedDetails.ownerId !== (user?.id || user?.email || null)) {
          setConnectionError(
            "The previous speaking session belongs to a different account. Go back before retrying."
          );
          return false;
        }
        isConnectingRef.current = true;
        setConnectionState("connecting");
        const previousAttempt = activeConnectAttemptIdRef.current;
        const previousIdentity = authTokenCacheIdentityRef.current;
        let settled = false;
        let recoveryMessage = failedDetails.metered
          ? "Your previous speaking session is still finishing. Please try again shortly."
          : SESSION_RECOVERY_UNCONFIRMED_MESSAGE;
        try {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          // Unlimited replays need capability-authorized room closure; metered
          // replays must additionally prove that the owned usage row settled.
          if (failedDetails.metered || failedDetails.shutdownCapability) {
            settled = await requestServerSessionShutdownForConnectionDetails({
              roomName: failedDetails.roomName,
              participantIdentity: failedDetails.participantName,
              shutdownCapability: failedDetails.shutdownCapability,
              source: "failed_session_retry",
              timeoutMs: SERVER_SESSION_SHUTDOWN_TIMEOUT_MS,
              logger,
              recover: failedDetails.metered === true,
              confirmRoomClosure: failedDetails.metered === false,
              accessToken: token,
            });
          }
        } catch (error) {
          if (error instanceof SessionRecoveryRoomStillExistsError) {
            recoveryMessage = error.message;
          }
          logger.warn("Failed-session recovery is unavailable", { error });
        } finally {
          isConnectingRef.current = false;
        }
        if (
          previousAttempt !== activeConnectAttemptIdRef.current ||
          previousIdentity !== authTokenCacheIdentityRef.current ||
          activeConnectionDetailsRef.current !== failedDetails
        )
          return false;
        if (getRecoveryScopeRef.current(supportedSessionMode) !== recoveryScope) {
          setConnectionState("failed");
          setConnectionError("Session setup changed during Retry. Go back before starting again.");
          return false;
        }
        if (!settled) {
          setConnectionState("failed");
          setConnectionError(recoveryMessage);
          return false;
        }
        activeConnectionDetailsRef.current = null;
        tokenCacheRef.current = null;
        unresolvedStartRef.current = null;
        unresolvedStart = null;
        retryIssuanceStartAttemptIdRef.current = null;
      }

      if (hasActiveLiveKitConnection(currentRoomRef.current.state)) {
        logger.info("LiveKit room is already active, ignoring duplicate start request", {
          roomState: currentRoomRef.current.state,
          effectiveSessionMode: supportedSessionMode,
        });
        markLatencyPhase("connect_skipped_active_room", {
          roomState: currentRoomRef.current.state,
        });
        return false;
      }

      // Remember mode for potential auto-retry
      lastRequestedModeRef.current = supportedSessionMode;
      connectAttemptSeqRef.current = attemptId;
      activeConnectAttemptIdRef.current = attemptId;
      const issuanceStartAttemptId =
        unresolvedStart?.id ||
        retryIssuanceStartAttemptIdRef.current ||
        createSessionLatencyTraceId();
      retryIssuanceStartAttemptIdRef.current = null;
      connectCancelOriginRef.current = null;
      // Local retries may share a server Start ID, but only the current,
      // uncancelled local attempt may commit response or failure state.
      const isCurrentAttempt = () =>
        attemptId === activeConnectAttemptIdRef.current &&
        getConnectCancelOrigin(attemptId) === "none";
      const assertCurrentAttempt = () => {
        if (!isCurrentAttempt()) {
          throw new StaleLiveKitConnectionAttemptError(
            attemptId,
            activeConnectAttemptIdRef.current
          );
        }
      };
      connectionFailureStickyRef.current = false;
      handledConnectionExceptionReportedRef.current = false;

      // Clear any previous errors
      setConnectionError(null);
      activeSessionUsageRef.current = null;
      setActiveSessionUsage(null);

      // Redirect unauthenticated users to /auth unless guest sessions are explicitly enabled.
      if (!user) {
        if (!allowUnauthenticatedSessions) {
          logger.info("Unauthenticated user attempting to connect. Opening Auth Modal");
          openAuthModal();
          return false;
        }

        if (!unresolvedStart && hasExceededGuestLimit) {
          logger.info("Guest user has exceeded usage limit. Preventing connection.");
          showFreemiumLimitPopup("guest");
          return false;
        }
      }

      /*
    // Guest limit check removed as unauth users are now redirected
    if (!user && hasExceededGuestLimit) {
      logger.info("Guest user has exceeded their usage limit. Preventing connection.");
      setShowTimeLimitPopup(true);
      return;
    } 
    */

      if (!unresolvedStart && user && subscribed === false && hasExceededRegisteredLimit) {
        logger.info(
          "Registered user (unsubscribed) has exceeded daily usage. Preventing connection."
        );
        showFreemiumLimitPopup("daily", freemiumUsageState);
        return false;
      }

      if (!unresolvedStart && user && subscribed === false) {
        const canStartFreemiumSession = await evaluateFreemiumStartGate(supportedSessionMode, {
          allowPreSessionOffer: false,
        });
        if (!canStartFreemiumSession || !isCurrentAttempt()) {
          return false;
        }
      }

      startConnectionElapsedTimer();

      // Check if we have a valid cached token
      const now = Date.now();
      const cachedToken = tokenCacheRef.current;
      const currentTutorId = tutorId || undefined;
      const currentTutorPrompt = tutorPrompt || undefined;
      const currentTutorCurriculumItemId = tutorCurriculumItemId || null;
      const sessionVocabularyParam = serializeSessionVocabularyContext(sessionVocabularyContext);
      const sessionVocabularyKey = getSessionVocabularyCacheKey(sessionVocabularyContext);
      const cacheConnectionDetails = (connectionDetails: LiveKitConnectionDetailsTokenPayload) => {
        assertCurrentAttempt();
        tokenCacheRef.current = buildLiveKitAgentTokenCacheEntry({
          connectionDetails,
          languageCode: selectedLanguageCode,
          sessionMode: effectiveSessionMode,
          learningMode,
          tutorWhiteboardEnabled,
          examplesBeforePractice,
          languageLevel: selectedLanguageLevel,
          fromLanguageCode,
          fromLanguageName,
          voice: selectedVoice,
          avatarId: avatarId || undefined,
          teachingStyle: selectedTeachingStyle,
          nickname,
          customInstructions,
          noAvatar,
          model: selectedModel,
          tutorId: currentTutorId,
          tutorPrompt: currentTutorPrompt,
          tutorCurriculumItemId: currentTutorCurriculumItemId,
          journeyChapterId: journeyChapterId || null,
          podcastDiscussionId: podcastDiscussionId || null,
          sessionVocabularyKey,
          nowMs: now,
          cacheVersion: TOKEN_CACHE_VERSION,
        });
      };
      const startManagedSessionFromConnectionDetails = async (
        connectionDetails: LiveKitConnectionDetailsTokenPayload
      ) => {
        assertCurrentAttempt();
        activeSessionUsageRef.current = connectionDetails.usage;
        setActiveSessionUsage(connectionDetails.usage);
        await startManagedSession(
          {
            participantToken: connectionDetails.participantToken,
            roomName: connectionDetails.roomName,
            serverUrl: connectionDetails.serverUrl,
            participantName: connectionDetails.participantName,
            shutdownCapability: connectionDetails.shutdownCapability,
            tokenSource: connectionDetails.tokenSource,
            metered: connectionDetails.usage?.policy === "freemium",
            recoveryScope,
            ownerId: user?.id || user?.email || null,
          },
          effectiveSessionMode,
          attemptId
        );
      };

      // Validate cached token is still usable
      const isCachedTokenValid = isLiveKitAgentTokenCacheEntryValid({
        cachedToken,
        cacheVersion: TOKEN_CACHE_VERSION,
        languageCode: selectedLanguageCode,
        sessionMode: effectiveSessionMode,
        learningMode,
        tutorWhiteboardEnabled,
        examplesBeforePractice,
        languageLevel: selectedLanguageLevel,
        fromLanguageCode,
        fromLanguageName,
        voice: selectedVoice,
        avatarId: avatarId || undefined,
        teachingStyle: selectedTeachingStyle,
        nickname,
        customInstructions,
        noAvatar,
        model: selectedModel,
        tutorId: currentTutorId,
        tutorPrompt: currentTutorPrompt,
        tutorCurriculumItemId: currentTutorCurriculumItemId,
        journeyChapterId: journeyChapterId || null,
        podcastDiscussionId: podcastDiscussionId || null,
        sessionVocabularyKey,
        nowMs: now,
        isRoomConnected: currentRoomRef.current.state === ConnectionState.Connected,
      });

      if (
        isCachedTokenValid &&
        cachedToken &&
        process.env.NEXT_PUBLIC_GUEST_VOICE_RUNTIME_ENABLED !== "true"
      ) {
        try {
          markLatencyPhase("token_cache_hit", {
            roomName: cachedToken.roomName,
          });
          logger.info("Resuming previous session with cached token", {
            sessionMode: cachedToken.sessionMode,
            languageCode: selectedLanguageCode,
            fromLanguageCode: fromLanguageCode,
            fromLanguageName: fromLanguageName,
            voice: selectedVoice,
            avatarId: avatarId || undefined,
            teachingStyle: selectedTeachingStyle,
            hasNickname: Boolean(nickname),
            hasCustomInstructions: Boolean(customInstructions),
            noAvatar,
            model: selectedModel,
            tutorCurriculumItemId: currentTutorCurriculumItemId,
            journeyChapterId: journeyChapterId || null,
            podcastDiscussionId: podcastDiscussionId || null,
            hasSessionVocabularyContext: Boolean(sessionVocabularyParam),
            tokenAgeMs: now - (cachedToken.expiresAt - 10 * 60 * 1000),
            continueFromPrevious: true,
          });
          isConnectingRef.current = true;
          setConnectionState("connecting");

          // Start a 15s timeout guard for cached token path
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          connectionTimeoutRef.current = setTimeout(() => {
            const currentRoom = currentRoomRef.current;
            if (
              attemptId === activeConnectAttemptIdRef.current &&
              (currentRoom.state === ConnectionState.Connecting || isConnectingRef.current)
            ) {
              markConnectCancelOrigin(attemptId, "timeout_guard");
              logger.warn(
                "Cached-token connection timed out after 15s. Resetting and retrying...",
                {
                  attemptId,
                  roomName: cachedToken.roomName,
                  roomState: currentRoom.state,
                }
              );
              isConnectingRef.current = false;
              try {
                if (currentRoom.state === ConnectionState.Connecting) {
                  currentRoom.disconnect();
                }
              } catch {}
              // Drop cached token to force fresh fetch
              tokenCacheRef.current = null;
              // Stop UI timer
              setIsRecording(false);
              if (
                !scheduleConnectRetry(
                  attemptId,
                  "cached_token_timeout_guard",
                  "Connection timed out. Retrying...",
                  issuanceStartAttemptId
                )
              ) {
                connectionFailureStickyRef.current = true;
                setConnectionError("Connection timed out. Please try again.");
                setConnectionState("failed");
                resetConnectionElapsedTimer();
                logger.error("Max auto-retry attempts reached (cached token path)", {
                  attemptId,
                });
              }
            }
          }, 15000);

          await startManagedSessionFromConnectionDetails({
            participantToken: cachedToken.token,
            roomName: cachedToken.roomName,
            serverUrl: cachedToken.url,
            participantName: cachedToken.participantName,
            shutdownCapability: cachedToken.shutdownCapability,
            tokenSource: cachedToken.tokenSource,
            usage: cachedToken.usage,
          });
          return isCurrentAttempt();
        } catch (error) {
          if (!isCurrentAttempt()) return false;
          activeSessionUsageRef.current = null;
          setActiveSessionUsage(null);
          markLatencyPhase("token_cache_connect_failed", {
            error: getErrorMessage(error),
          });
          const cancelOrigin = getConnectCancelOrigin(attemptId);
          const decision = decideLiveKitConnectFailure({
            attemptId,
            activeAttemptId: activeConnectAttemptIdRef.current,
            cancelOrigin,
            error,
            retryCount: connectRetryCountRef.current,
            maxRetries: MAX_CONNECT_AUTO_RETRIES,
            roomState: currentRoomRef.current.state,
          });
          logger.error("Failed to connect with cached token:", {
            error,
            attemptId,
            decision,
            roomState: currentRoomRef.current.state,
            cancelOrigin,
          });
          tokenCacheRef.current = null;

          if (decision.action === "ignore") {
            return decision.reason === "active_room";
          }

          if (decision.action === "retry") {
            scheduleConnectRetry(
              attemptId,
              `cached_token_${decision.reason}`,
              decision.message,
              issuanceStartAttemptId
            );
            return false;
          }

          // Continue to fetch a new token below for non-cancellation cached-token failures.
        } finally {
          if (attemptId === activeConnectAttemptIdRef.current) {
            isConnectingRef.current = false;
          }
          if (attemptId === activeConnectAttemptIdRef.current && connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
        }
      }

      markLatencyPhase("token_cache_miss", {
        hasCachedToken: Boolean(cachedToken),
      });

      logger.info("Fetching new connection details...", {
        reason: cachedToken ? "Session parameters changed" : "No cached token",
        sessionMode: effectiveSessionMode,
        learningMode,
        languageCode: selectedLanguageCode,
        fromLanguageCode: fromLanguageCode,
        fromLanguageName: fromLanguageName,
        voice: selectedVoice,
        avatarId: avatarId || undefined,
        teachingStyle: selectedTeachingStyle,
        hasNickname: Boolean(nickname),
        hasCustomInstructions: Boolean(customInstructions),
        noAvatar,
        model: selectedModel,
        previousSessionMode: cachedToken?.sessionMode,
        previousLearningMode: cachedToken?.learningMode,
        previousLanguageCode: cachedToken?.languageCode,
        previousFromLanguageCode: cachedToken?.fromLanguageCode,
        previousFromLanguageName: cachedToken?.fromLanguageName,
        previousVoice: cachedToken?.voice,
        previousAvatarId: cachedToken?.avatarId,
        previousTeachingStyle: cachedToken?.teachingStyle,
        previousHasNickname: Boolean(cachedToken?.nickname),
        previousHasCustomInstructions: Boolean(cachedToken?.customInstructions),
        previousNoAvatar: cachedToken?.noAvatar,
        previousModel: cachedToken?.model,
        tutorId: currentTutorId,
        hasCustomTutorPrompt: !!currentTutorPrompt,
        hasSessionVocabularyContext: Boolean(sessionVocabularyParam),
      });
      try {
        isConnectingRef.current = true;
        setConnectionState("connecting");

        // Set a timeout for the connection (new token path)
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        connectionTimeoutRef.current = setTimeout(() => {
          const currentRoom = currentRoomRef.current;
          if (
            attemptId === activeConnectAttemptIdRef.current &&
            (currentRoom.state === ConnectionState.Connecting || isConnectingRef.current)
          ) {
            markConnectCancelOrigin(attemptId, "timeout_guard");
            logger.warn(
              `Connection attempt timed out after ${FRESH_CONNECTION_TIMEOUT_MS}ms. Resetting and retrying...`,
              {
                attemptId,
                roomName: activeConnectionDetailsRef.current?.roomName || currentRoom.name || null,
                roomState: currentRoom.state,
              }
            );
            isConnectingRef.current = false;
            try {
              if (currentRoom.state === ConnectionState.Connecting) {
                currentRoom.disconnect();
              }
            } catch {}
            // Invalidate cached token just in case
            tokenCacheRef.current = null;
            // Stop the UI timer
            setIsRecording(false);
            if (
              !scheduleConnectRetry(
                attemptId,
                "fresh_token_timeout_guard",
                "Connection timed out. Retrying...",
                issuanceStartAttemptId
              )
            ) {
              connectionFailureStickyRef.current = true;
              setConnectionError("Connection timed out. Please try again.");
              setConnectionState("failed");
              resetConnectionElapsedTimer();
              logger.error("Max auto-retry attempts reached.", {
                attemptId,
              });
            }
          }
        }, FRESH_CONNECTION_TIMEOUT_MS);

        let sessionUser = null;
        let accessToken: string | undefined;
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          sessionUser = session?.user ?? null;
          accessToken = session?.access_token;
        } catch (authError) {
          logger.error("Failed to get auth session:", authError);
        }

        const effectiveUser = resolveEffectiveAuthUser(user, sessionUser);

        // A newly authenticated learner may still be claiming the immediately
        // preceding guest demo. Give that tutor-only summary a short bounded
        // head start so the fresh session can open with welcome-back context.
        if (effectiveUser?.id) {
          await waitForActiveGuestSessionClaim();
        }

        const selectedLanguageName =
          sessionLanguageName || getLanguageNameByCode(selectedLanguageCode);
        const clientId = getOrCreateClientId();
        const preparedRequest =
          unresolvedStart?.request ??
          buildConnectionDetailsRequest({
            endpoint: process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? "/api/connection-details",
            origin: typeof window !== "undefined" ? window.location.origin : "",
            languageCode: selectedLanguageCode,
            languageName: selectedLanguageName,
            languageLevel: selectedLanguageLevel,
            voice: selectedVoice,
            avatarId: avatarId || undefined,
            teachingStyle: selectedTeachingStyle,
            nickname,
            customInstructions,
            noAvatar,
            sessionMode: effectiveSessionMode,
            learningMode,
            mathsDemoId,
            mathsFixtureRevision,
            tutorWhiteboardEnabled,
            examplesBeforePractice,
            model: selectedModel,
            conversationId: conversationId || undefined,
            resumeFromConversationId: resumeFromConversationId || undefined,
            journeyChapterId,
            podcastDiscussionId,
            sessionVocabularyParam: sessionVocabularyParam || undefined,
            fromLanguageName: fromLanguageName || undefined,
            fromLanguageCode: fromLanguageCode || undefined,
            effectiveUser,
            sessionUser,
            contextUser: user,
            memories,
            getMemoriesSummary,
            qdrantContext,
            classId,
            teacherPreferences,
            tutorId: currentTutorId,
            tutorPrompt: currentTutorPrompt,
            tutorCurriculumItemId: currentTutorCurriculumItemId,
            launchSurface,
            prewarmPolicy,
            clientId,
            accessToken,
            clientSubscriptionAccess: subscribed,
            clientTraceId: latencyTraceRef.current?.traceId || "",
            startAttemptId: issuanceStartAttemptId,
            clientStartMs: latencyTraceRef.current?.clientStartMs,
            logger,
          });

        const { url, method, body, preloadedMemoriesCount, preloadedQdrant } = preparedRequest;
        const headers = { ...preparedRequest.headers };
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

        if (!unresolvedStart && url.origin === window.location.origin) {
          if (!effectiveUser?.id && (await prepareGuestVoiceStart(posthogClient))) {
            headers["x-guest-voice-start"] = "1";
          }
          if (guestVoiceAnalyticsAllowed(posthogClient)) headers["x-guest-voice-analytics"] = "1";
        }
        if (!isCurrentAttempt()) return false;
        unresolvedStartRef.current = {
          id: issuanceStartAttemptId,
          scope: recoveryScope,
          request: { ...preparedRequest, headers },
        };

        logger.info("Making connection request with session mode", {
          sessionMode: effectiveSessionMode,
          selectedLanguageCode,
          noAvatar,
          preloadedMemories: preloadedMemoriesCount,
          preloadedQdrant,
          endpointHost: url.host || null,
          endpointPath: url.pathname,
          requestMethod: method,
          requestBodyCharacters: body.length,
        });

        logger.info("Fetching connection details...");

        const parseConnectionDetailsResponse = async (connectionDetailsResponse: Response) => {
          const payload = await connectionDetailsResponse.json();
          assertCurrentAttempt();
          if (
            unresolvedStart &&
            getRecoveryScopeRef.current(supportedSessionMode) !== recoveryScope
          ) {
            throw new Error(
              "Account or session setup changed during Retry. Go back before starting again."
            );
          }
          registerGuestVoiceAnalytics(posthogClient, payload.voiceRuntimeExperiment);
          const parsed = toLiveKitConnectionDetailsTokenPayload(payload);
          unresolvedStartRef.current = null;
          return parsed;
        };
        const cacheAndStartConnectionDetails = async (
          connectionDetails: LiveKitConnectionDetailsTokenPayload
        ) => {
          cacheConnectionDetails(connectionDetails);
          await startManagedSessionFromConnectionDetails(connectionDetails);
        };

        const recoverFromUnauthorizedConnectionDetails = async (): Promise<boolean> => {
          const recovered = await recoverUnauthorizedConnectionDetails({
            isCurrentAttempt,
            url,
            method,
            headers,
            body,
            getRetryPhase: () => auth401RetryPhaseRef.current,
            setRetryPhase: (phase) => {
              auth401RetryPhaseRef.current = phase;
            },
            maxRetryPhases: MAX_401_RETRY_PHASES,
            hasCachedToken: () => Boolean(tokenCacheRef.current),
            clearTokenCache: () => {
              tokenCacheRef.current = null;
            },
            isRetryingAsGuest,
            setIsRetryingAsGuest,
            setConnectionError,
            setConnectionState,
            setIsConnected,
            setAgentState,
            setIsRecording,
            resetConnectionElapsedTimer,
            refreshSession: () => supabase.auth.refreshSession(),
            parseConnectionDetailsResponse,
            cacheAndStartConnectionDetails,
            posthogClient,
            hasPosthogKey: Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY),
            selectedLanguageCode,
            userType: user ? "authenticated" : "guest",
            logger,
          });
          if (!isCurrentAttempt()) return false;
          if (!recovered) {
            connectionFailureStickyRef.current = true;
          }
          return recovered;
        };

        markLatencyPhase("connection_details_request_started", {
          clientId,
        });
        connectionFailurePhaseRef.current = "connection_details_fetch";
        let response: Response;
        let connectionDetailsFetchAttempts = 0;
        const connectionDetailsFetchStartedAt = Date.now();
        try {
          const fetchResult = await fetchConnectionDetailsWithRetry({
            input: url.toString(),
            init: {
              method,
              headers,
              body,
              cache: "no-store",
            },
            onRetry: ({ attempt, failureKind, nextAttempt }) => {
              assertCurrentAttempt();
              markLatencyPhase("connection_details_fetch_retrying", {
                attempt,
                failureKind,
                nextAttempt,
              });
              captureLiveKitConnectionEvent("connection_details_fetch_retrying", undefined, {
                endpoint_path: url.pathname,
                endpoint_host: url.host || null,
                attempt,
                next_attempt: nextAttempt,
                failure_kind: failureKind,
                timeout_ms: CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
              });
            },
          });
          response = fetchResult.response;
          connectionDetailsFetchAttempts = fetchResult.attemptCount;
        } catch (fetchError) {
          if (!isCurrentAttempt()) return false;
          captureLiveKitConnectionEvent("connection_details_fetch_failed", fetchError, {
            endpoint_path: url.pathname,
            endpoint_host: url.host || null,
            request_duration_ms: Date.now() - connectionDetailsFetchStartedAt,
            has_authorization_header: Boolean(accessToken),
            fetch_attempts:
              fetchError instanceof ConnectionDetailsFetchError ? fetchError.attempts : 1,
            failure_kind:
              fetchError instanceof ConnectionDetailsFetchError
                ? fetchError.failureKind
                : "unknown",
            timeout_ms: CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
          });
          throw fetchError;
        }
        if (!isCurrentAttempt()) return false;
        markLatencyPhase("connection_details_response_received", {
          status: response.status,
          requestDurationMs: Date.now() - connectionDetailsFetchStartedAt,
          fetchAttempts: connectionDetailsFetchAttempts,
        });

        if (!response.ok) {
          const errorText = await response.text();
          if (!isCurrentAttempt()) return false;
          connectionFailurePhaseRef.current = "connection_details_http";

          // Enhanced 401 response handling with phased retry strategy
          if (response.status === 401) {
            return recoverFromUnauthorizedConnectionDetails();
          }

          let errorPayload: unknown = null;
          try {
            errorPayload = JSON.parse(errorText);
          } catch {}
          const issuanceInProgressRetry = parseVoiceSessionIssuanceInProgressRetry({
            status: response.status,
            payload: errorPayload,
            retryAfterHeader: response.headers.get("Retry-After"),
          });
          if (issuanceInProgressRetry) {
            // Keep this ID even after the bounded automatic retry budget is
            // exhausted. A learner pressing Retry is still recovering the
            // same explicit Start and must not bypass the server lease.
            retryIssuanceStartAttemptIdRef.current = issuanceStartAttemptId;
            const retryScheduled = scheduleConnectRetry(
              attemptId,
              "issuance_in_progress",
              "Your speaking session is still being prepared. Retrying...",
              issuanceStartAttemptId,
              issuanceInProgressRetry.retryAfterMs
            );
            captureLiveKitConnectionEvent("connection_details_issuance_in_progress", undefined, {
              endpoint_path: url.pathname,
              endpoint_host: url.host || null,
              status: response.status,
              retry_scheduled: retryScheduled,
              retry_after_ms: issuanceInProgressRetry.retryAfterMs,
              request_duration_ms: Date.now() - connectionDetailsFetchStartedAt,
            });
            if (!retryScheduled) {
              connectionFailureStickyRef.current = true;
              setConnectionState("failed");
              setConnectionError(
                "Your speaking session is still being prepared. Please try again shortly."
              );
              resetConnectionElapsedTimer();
            }
            return false;
          }
          const freeLimitResponse = parseFreeVoiceLimitResponse(response.status, errorPayload);
          if (freeLimitResponse) {
            unresolvedStartRef.current = null;
            activeSessionUsageRef.current = null;
            setActiveSessionUsage(null);
            setIsConnected(false);
            setIsRecording(false);
            setAgentState("disconnected");
            setConnectionState("disconnected");
            setConnectionError(null);
            connectionFailurePhaseRef.current = "idle";
            resetConnectionElapsedTimer();
            setFreemiumUsageState((current) =>
              current
                ? {
                    ...current,
                    dailyRemainingMinutes: 0,
                    shouldShowPreSessionOffer: false,
                  }
                : current
            );
            if (user) {
              setHasExceededRegisteredLimit(true);
              showFreemiumLimitPopup("daily", freemiumUsageState);
            } else {
              setHasExceededGuestLimit(true);
              showFreemiumLimitPopup("guest");
            }
            captureLiveKitConnectionEvent("connection_details_free_limit_reached", undefined, {
              endpoint_path: url.pathname,
              endpoint_host: url.host || null,
              status: response.status,
              code: freeLimitResponse.code,
              request_duration_ms: Date.now() - connectionDetailsFetchStartedAt,
            });
            return false;
          }

          captureLiveKitConnectionEvent(
            "connection_details_http_failed",
            new Error(`Server responded with ${response.status}`),
            {
              endpoint_path: url.pathname,
              endpoint_host: url.host || null,
              status: response.status,
              request_duration_ms: Date.now() - connectionDetailsFetchStartedAt,
              // The recovery response may contain an exact-room shutdown proof.
              // Keep that proof out of telemetry and exception messages.
              response_text_preview:
                (errorPayload as { code?: string } | null)?.code === "VOICE_SESSION_UNAVAILABLE"
                  ? "VOICE_SESSION_UNAVAILABLE"
                  : errorText.slice(0, 500),
            }
          );
          const unavailable = errorPayload as {
            code?: string;
            previousSession?: {
              roomName?: string;
              participantName?: string;
              shutdownCapability?: string;
              metered?: boolean;
            };
          } | null;
          const retainIssuance = shouldRetainVoiceSessionIssuance({
            status: response.status,
            payload: errorPayload,
            startAttemptId: issuanceStartAttemptId,
          });
          const replacementSafe = isVoiceSessionReplacementSafe({
            status: response.status,
            payload: errorPayload,
            startAttemptId: issuanceStartAttemptId,
          });
          if (
            unavailable?.code === "VOICE_SESSION_UNAVAILABLE" &&
            unavailable.previousSession?.roomName &&
            unavailable.previousSession.participantName
          ) {
            activeConnectionDetailsRef.current = {
              participantToken: "",
              serverUrl: "",
              ...unavailable.previousSession,
              roomName: unavailable.previousSession.roomName,
              participantName: unavailable.previousSession.participantName,
              // Responses predating the explicit policy flag were metered.
              metered: unavailable.previousSession.metered !== false,
              requiresRecovery: true,
              recoveryScope,
              ownerId: user?.id || user?.email || null,
            };
            unresolvedStartRef.current = null;
          } else if (!retainIssuance) {
            unresolvedStartRef.current = null;
            retryIssuanceStartAttemptIdRef.current = null;
          }
          throw new Error(
            unavailable?.code === "VOICE_SESSION_UNAVAILABLE"
              ? "The original speaking session is no longer available. Retry to finish its cleanup."
              : retainIssuance || replacementSafe
                ? // Preserve normal failure telemetry, but don't let an uncertain
                  // provider error's wording trigger a fresh automatic connection.
                  `Server responded with ${response.status}`
                : `Server responded with ${response.status}: ${errorText}`
          );
        }

        const connectionDetails = await parseConnectionDetailsResponse(response);
        if (!isCurrentAttempt()) return false;
        unresolvedStartRef.current = null;
        markLatencyPhase("connection_details_payload_parsed", {
          roomName: connectionDetails.roomName,
          tokenSource: connectionDetails.tokenSource || "unknown",
        });
        activeConnectionDetailsRef.current = {
          participantToken: connectionDetails.participantToken,
          roomName: connectionDetails.roomName,
          serverUrl: connectionDetails.serverUrl,
          participantName: connectionDetails.participantName,
          shutdownCapability: connectionDetails.shutdownCapability,
          tokenSource: connectionDetails.tokenSource,
          metered: connectionDetails.usage?.policy === "freemium",
          recoveryScope,
          ownerId: user?.id || user?.email || null,
        };

        // Cache the token - assume it's valid for 10 minutes from now
        // (the actual token might have a different expiry, but this is a safe estimate)
        await cacheAndStartConnectionDetails(connectionDetails);

        return isCurrentAttempt();
      } catch (error) {
        if (!isCurrentAttempt()) return false;
        activeSessionUsageRef.current = null;
        setActiveSessionUsage(null);
        markLatencyPhase("connect_failed", {
          error: getErrorMessage(error),
        });

        // Treat this token as bad/outdated so the next attempt fetches a fresh one
        tokenCacheRef.current = null;

        const errorMessage = getErrorMessage(error);
        const lowerMsg = errorMessage.toLowerCase();
        const alreadyHandled401 = lowerMsg.includes("401") && auth401RetryPhaseRef.current === 0;
        const cancelOrigin = getConnectCancelOrigin(attemptId);
        const decision = decideLiveKitConnectFailure({
          attemptId,
          activeAttemptId: activeConnectAttemptIdRef.current,
          cancelOrigin,
          error,
          retryCount: connectRetryCountRef.current,
          maxRetries: MAX_CONNECT_AUTO_RETRIES,
          roomState: currentRoomRef.current.state,
          tokenIssueAlreadyHandled: alreadyHandled401,
        });

        logger.error("Failed to connect to LiveKit:", {
          error,
          attemptId,
          activeAttemptId: activeConnectAttemptIdRef.current,
          decision,
          cancelOrigin,
          roomName:
            activeConnectionDetailsRef.current?.roomName || currentRoomRef.current.name || null,
          roomState: currentRoomRef.current.state,
        });

        if (decision.action === "ignore") {
          if (decision.reason === "active_room") {
            setIsConnected(true);
            setConnectionState("connected");
            setConnectionError(null);
            connectionFailureStickyRef.current = false;
            return true;
          }

          return false;
        }

        setIsConnected(false);
        setAgentState("disconnected");
        // Stop the UI timer on failure or retry.
        setIsRecording(false);

        if (decision.action === "retry") {
          if (
            decision.reason === "microphone_negotiation" &&
            hasActiveLiveKitConnection(currentRoomRef.current.state)
          ) {
            logger.warn("Disconnecting active room before microphone-negotiation retry", {
              attemptId,
              roomName:
                activeConnectionDetailsRef.current?.roomName || currentRoomRef.current.name || null,
              roomState: currentRoomRef.current.state,
            });
            try {
              currentRoomRef.current.disconnect();
            } catch (disconnectError) {
              logger.warn("Failed to disconnect active room before microphone-negotiation retry", {
                attemptId,
                error:
                  disconnectError instanceof Error
                    ? disconnectError.message
                    : String(disconnectError),
              });
            }
          }
          scheduleConnectRetry(
            attemptId,
            decision.reason,
            decision.message,
            issuanceStartAttemptId
          );
        } else {
          connectionFailureStickyRef.current = true;
          setConnectionState("failed");
          setConnectionError(getUserFacingConnectionFailureMessage(decision.message));
          resetConnectionElapsedTimer();
        }

        captureLiveKitConnectionEvent("livekit_connection_failed", error, {
          decision_action: decision.action,
          decision_reason: "reason" in decision ? decision.reason : null,
        });
        return false;
      } finally {
        if (attemptId === activeConnectAttemptIdRef.current) {
          isConnectingRef.current = false;
        }
        if (attemptId === activeConnectAttemptIdRef.current && connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
      }
    },
    [
      allowUnauthenticatedSessions,
      avatarId,
      captureLiveKitConnectionEvent,
      classId,
      conversationId,
      resumeFromConversationId,
      customInstructions,
      evaluateFreemiumStartGate,
      freemiumUsageState,
      fromLanguageCode,
      fromLanguageName,
      getConnectCancelOrigin,
      getMemoriesSummary,
      hasExceededGuestLimit,
      hasExceededRegisteredLimit,
      isRetryingAsGuest,
      journeyChapterId,
      learningMode,
      latencyTraceRef,
      launchSurface,
      mathsDemoId,
      mathsFixtureRevision,
      markConnectCancelOrigin,
      markLatencyPhase,
      memories,
      nickname,
      noAvatar,
      openAuthModal,
      podcastDiscussionId,
      posthogClient,
      prewarmPolicy,
      qdrantContext,
      resetConnectionElapsedTimer,
      scheduleConnectRetry,
      selectedLanguageCode,
      sessionLanguageName,
      selectedLanguageLevel,
      selectedModel,
      selectedTeachingStyle,
      selectedVoice,
      sessionVocabularyContext,
      showFreemiumLimitPopup,
      startConnectionElapsedTimer,
      startLatencyTrace,
      startManagedSession,
      subscribed,
      teacherPreferences,
      tutorCurriculumItemId,
      tutorId,
      tutorPrompt,
      tutorWhiteboardEnabled,
      examplesBeforePractice,
      user,
    ]
  );

  const connectToLiveKit = useMemo(
    () =>
      withLessonRefreshProtection((mode: SessionMode) => {
        if (recoveryConnectRef.current) return recoveryConnectRef.current;
        const recovering = Boolean(
          unresolvedStartRef.current ||
          activeConnectionDetailsRef.current?.requiresRecovery ||
          connectionFailureStickyRef.current
        );
        const pending = connectToLiveKitImpl(mode);
        if (recovering) {
          recoveryConnectRef.current = pending;
          const clear = () => {
            if (recoveryConnectRef.current === pending) recoveryConnectRef.current = null;
          };
          void pending.then(clear, clear);
        }
        return pending;
      }),
    [connectToLiveKitImpl]
  );

  useEffect(() => {
    connectToLiveKitCommandRef.current = connectToLiveKit;
  }, [connectToLiveKit]);

  const {
    handleSpatiusPrimaryConnected,
    handleSpatiusPrimaryDisconnected,
    handleSpatiusPrimaryError,
    handleSpatiusPrimaryStateChange,
  } = useLiveKitSpatiusPrimaryEvents<SpatiusPrimarySession>({
    currentRoomRef,
    spatiusPrimaryViewerRoomRef,
    spatiusPrimaryParticipantConnectedHandlerRef,
    spatiusPrimaryTrackPublishedHandlerRef,
    spatiusPrimaryTrackSubscribedHandlerRef,
    spatiusPrimaryConnectedRef,
    connectionFailurePhaseRef,
    setExternalRoom,
    setSpatiusPrimarySession,
    clearSpatiusPrimaryFallbackTimer,
    clearSpatiusPrimaryBaseAudioWatchdogTimer,
    syncSpatiusPrimaryViewerSubscriptions,
    scheduleSpatiusPrimaryBaseAudioWatchdog,
    markLatencyPhase,
    captureLiveKitConnectionEvent,
    fallbackToAudioOnly,
    logger,
  });

  useLiveKitConnectionStateMonitor({
    currentRoom,
    currentRoomRef,
    isConnectedRef,
    isRecordingRef,
    isConnectingRef,
    freshReconnectInProgressRef,
    freshReconnectTimerRef,
    websocket1006RetryCountRef,
    connectionFailureStickyRef,
    connectionFailurePhaseRef,
    activeConnectAttemptIdRef,
    tokenCacheRef,
    lastRequestedModeRef,
    maxWebsocket1006Retries: MAX_WEBSOCKET_1006_RETRIES,
    isRecording,
    selectedLanguageCode,
    user,
    posthogClient,
    connectToLiveKit,
    markLatencyPhase,
    setConnectionState,
    setConnectionError,
    setConnectionQuality,
    setIsConnected,
    setIsRecording,
    logger,
  });

  useLiveKitBaseRoomEffects({
    currentRoom,
    baseRoom: room,
    isConnected,
    isRecording,
    selectedLanguageCode,
    agentState,
    shouldUseSpatiusPrimaryRoom,
    audioPlaybackBlockedMessage: AUDIO_PLAYBACK_BLOCKED_MESSAGE,
    latencyAgentStateMarksRef,
    firstRemoteAudioMarkedRef,
    clearSpatiusPrimaryBaseAudioWatchdogTimer,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    logDuplicateAudioPublications,
    syncSpatiusPrimaryBaseRoomAudioSubscriptions,
    markLatencyPhase,
    setAudioTrackError,
    logger,
  });

  const startAudioPlayback = useCallback(
    async ({ suppressErrorMessage = false }: { suppressErrorMessage?: boolean } = {}) => {
      const result = await startRoomAudioPlayback(currentRoom);

      if (result.ok) {
        setAudioTrackError((prev) => (prev === AUDIO_PLAYBACK_BLOCKED_MESSAGE ? null : prev));
        return true;
      }

      logger.warn("Failed to start LiveKit audio playback", {
        error: result.error.message,
      });

      if (!suppressErrorMessage) {
        setAudioTrackError(AUDIO_PLAYBACK_BLOCKED_MESSAGE);
      }

      return false;
    },
    [currentRoom]
  );

  // Retry audio track initialization
  const retryAudioTrack = useCallback(async () => {
    if (!currentRoom || !isConnected || audioRetryCount >= 3) {
      logger.warn("Cannot retry audio track - room not connected or max retries reached");
      return;
    }

    try {
      logger.info("Attempting to retry audio track initialization", {
        retryCount: audioRetryCount,
      });
      setAudioRetryCount((prev) => prev + 1);

      // Clear current audio error
      setAudioTrackError(null);

      if (shouldUseSpatiusPrimaryRoom) {
        const spatiusAudioResult = await ensureSpatiusPrimaryBaseRoomAudioPlayback(
          currentRoom,
          "manual_audio_retry",
          {
            startPlayback: true,
          }
        );

        if (spatiusAudioResult.avatarAudioPublicationCount > 0) {
          logger.info("Spatius avatar audio retry completed", spatiusAudioResult);
          return;
        }
      }

      if (!currentRoom.canPlaybackAudio) {
        const playbackStarted = await startAudioPlayback();
        if (playbackStarted) {
          logger.info("Audio playback restored after browser unlock");
        }
        return;
      }

      // Disable and re-enable microphone to reinitialize the track
      await currentRoom.localParticipant.setMicrophoneEnabled(false);

      // Wait a moment before re-enabling
      setTimeout(async () => {
        try {
          await currentRoom.localParticipant.setMicrophoneEnabled(true);
          logger.info("Audio track retry completed successfully");
        } catch (error) {
          logger.error("Failed to re-enable microphone during retry:", error);
          setAudioTrackError("Failed to restart microphone. Please check your device settings.");
        }
      }, 1000);
    } catch (error) {
      logger.error("Failed to retry audio track:", error);
      setAudioTrackError("Failed to restart audio. Please try disconnecting and reconnecting.");
    }
  }, [
    audioRetryCount,
    currentRoom,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    isConnected,
    shouldUseSpatiusPrimaryRoom,
    startAudioPlayback,
  ]);

  // Clear audio errors when disconnecting
  const clearAudioErrors = useCallback(() => {
    setAudioTrackError(null);
    setAudioRetryCount(0);
  }, []);

  const shutdownActiveSessionTransport = useCallback(
    (
      source: string,
      options: {
        keepalive?: boolean;
        timeoutMs?: number;
        localIsolationTimeoutMs?: number;
        sessionControlTimeoutMs?: number;
        room?: Room;
        attemptId?: number;
      } = {}
    ) => {
      const activeRoom = options.room ?? currentRoomRef.current;
      const attemptId = options.attemptId ?? activeConnectAttemptIdRef.current;

      const handle = sessionTransportShutdownCoordinator.run({
        attemptId,
        room: activeRoom,
        keepalive: options.keepalive === true,
        localIsolationTimeoutMs:
          options.localIsolationTimeoutMs ?? LOCAL_SESSION_ISOLATION_TIMEOUT_MS,
        sessionControlTimeoutMs:
          options.sessionControlTimeoutMs ?? SESSION_CONTROL_SHUTDOWN_TIMEOUT_MS,
        sendSessionControl: () =>
          sendSessionControlShutdownMessage({
            activeRoom,
            source,
            logger,
          }),
        requestServerShutdown: () =>
          requestServerSessionShutdownRequest({
            activeRoom,
            source,
            shutdownCapability:
              activeConnectionDetailsRef.current?.roomName === activeRoom.name
                ? activeConnectionDetailsRef.current.shutdownCapability
                : null,
            keepalive: options.keepalive,
            timeoutMs: options.timeoutMs ?? SERVER_SESSION_SHUTDOWN_TIMEOUT_MS,
            logger,
          }),
        resetViewerSession: () => resetSpatiusPrimarySession(`disconnect:${source}`),
        disconnectRoom: async () => {
          const microphonePublication = activeRoom.localParticipant.getTrackPublication(
            Track.Source.Microphone
          );
          try {
            microphonePublication?.track?.stop();
          } catch (error) {
            logger.warn("Failed to stop old microphone track before room isolation", {
              source,
              roomName: activeRoom.name || null,
              error: error instanceof Error ? error.message : String(error),
            });
          }
          await activeRoom.disconnect();
        },
      });
      holdLessonRefreshUntil(handle.cleanup);
      return handle;
    },
    [resetSpatiusPrimarySession, sessionTransportShutdownCoordinator]
  );

  // Disconnect from LiveKit room
  const disconnectFromLiveKit = useCallback(
    async (
      triggeredByTimer: boolean = false,
      invalidateTokenCache: boolean = false,
      source: string = "unspecified_disconnect",
      // Timer-triggered disconnects pass the duration they just computed;
      // recordingTime from this closure can lag after delayed callbacks.
      sessionDurationSeconds?: number
    ) => {
      markLatencyPhase("disconnect_requested", {
        triggeredByTimer,
        invalidateTokenCache,
        source,
      });
      logger.info("disconnectFromLiveKit called", {
        source,
        triggeredByTimer,
        invalidateTokenCache,
        isRecording,
        isConnected,
        roomName: currentRoomRef.current.name || null,
        roomState: currentRoomRef.current.state,
      });
      const activeRoom = currentRoomRef.current;
      const shutdownAttemptId = activeConnectAttemptIdRef.current;
      const roomNameAtDisconnect = activeRoom.name || null;
      const roomStateAtDisconnect = activeRoom.state;
      const currentSessionDuration = sessionDurationSeconds ?? recordingTime;
      const sessionUsageAtDisconnect = activeSessionUsageRef.current;
      const shutdownHandle = shutdownActiveSessionTransport(source, {
        room: activeRoom,
        attemptId: shutdownAttemptId,
      });
      activeSessionShutdownRef.current = {
        room: activeRoom,
        attemptId: shutdownAttemptId,
        handle: shutdownHandle,
      };

      if (isConnectingRef.current) {
        markConnectCancelOrigin(activeConnectAttemptIdRef.current, "explicit_disconnect");
      }
      const supersedingAttemptId = connectAttemptSeqRef.current + 1;
      connectAttemptSeqRef.current = supersedingAttemptId;
      activeConnectAttemptIdRef.current = supersedingAttemptId;
      isConnectingRef.current = false;
      if (connectionFailureStickyRef.current && activeConnectionDetailsRef.current?.metered) {
        activeConnectionDetailsRef.current.requiresRecovery = true;
      }
      connectionFailureStickyRef.current = false;
      // Clear any pending connection timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (freshReconnectTimerRef.current) {
        clearTimeout(freshReconnectTimerRef.current);
        freshReconnectTimerRef.current = null;
      }
      freshReconnectInProgressRef.current = false;

      resetConnectionElapsedTimer();
      if (invalidateTokenCache) {
        logger.debug("Invalidating token cache due to explicit disconnect request", {
          source,
        });
        tokenCacheRef.current = null;
      }

      // Always clear the token cache on disconnect to prevent stale token reuse
      // A fresh token will be fetched on the next connection attempt
      tokenCacheRef.current = null;

      // Reflect the learner's intent immediately. Transport signaling and
      // server cleanup continue against the captured old room.
      isConnectedRef.current = false;
      isRecordingRef.current = false;
      setIsConnected(false);
      setIsRecording(false);
      setAgentState("disconnected");
      setConnectionState("disconnecting");
      clearAudioErrors();
      hasTrackedEngagedSessionRef.current = false;
      setInteractionMode("passive");
      setRecordingTime(0);
      resetLatencyTrace();

      if (triggeredByTimer) {
        if (!user) {
          showFreemiumLimitPopup("guest");
          const storedUsageStr = localStorage.getItem(GUEST_USAGE_TIME_KEY);
          if (Number(storedUsageStr || "0") >= MAX_GUEST_SECONDS) {
            setHasExceededGuestLimit(true);
          }
        } else if (
          sessionUsageAtDisconnect?.policy === "freemium" &&
          sessionUsageAtDisconnect.dailyRemainingSeconds === 0
        ) {
          showFreemiumLimitPopup("daily", freemiumUsageState);
          if (user.email) {
            const used = getRegisteredUsageSeconds(user.email, logger);
            if (used >= MAX_REGISTERED_SECONDS_PER_DAY) {
              setHasExceededRegisteredLimit(true);
            }
          }
        }
      }
      activeSessionUsageRef.current = null;
      setActiveSessionUsage(null);

      const localIsolationPromise = shutdownHandle.localIsolation
        .then((result) => {
          replaceIsolatedBaseRoom(activeRoom);
          if (result.status !== "completed") {
            logger.warn("Continuing with a fresh LiveKit room after bounded local isolation", {
              source,
              roomName: roomNameAtDisconnect,
              isolationStatus: result.status,
              error:
                result.status === "failed"
                  ? result.error instanceof Error
                    ? result.error.message
                    : String(result.error)
                  : null,
            });
          }
          if (!isConnectingRef.current) {
            setConnectionState("disconnected");
          }
        })
        .finally(() => {
          if (activeLocalIsolationPromiseRef.current === localIsolationPromise) {
            activeLocalIsolationPromiseRef.current = null;
          }
        });
      activeLocalIsolationPromiseRef.current = localIsolationPromise;

      try {
        const { sessionControlSent, serverSessionShutdownRequested } = await shutdownHandle.cleanup;

        const lastSessionEndedCapture = lastSessionEndedCaptureRef.current;
        const sessionEndedCapturedRecently =
          lastSessionEndedCapture?.roomName === roomNameAtDisconnect &&
          Date.now() - lastSessionEndedCapture.capturedAtMs < 10_000;

        // PostHog event for session ended
        if (
          posthogClient &&
          process.env.NEXT_PUBLIC_POSTHOG_KEY &&
          currentSessionDuration > 0 &&
          sessionMode !== "maths" &&
          !sessionEndedCapturedRecently
        ) {
          lastSessionEndedCaptureRef.current = {
            roomName: roomNameAtDisconnect,
            capturedAtMs: Date.now(),
          };
          // Check if PostHog client is available and configured and session had duration
          posthogClient.capture("session_ended", {
            user_type: user ? "authenticated" : "guest",
            session_duration_seconds: currentSessionDuration,
            selected_language_code: selectedLanguageCode, // Add language code here too
            triggered_by_timer: triggeredByTimer,
            source,
            room_name: roomNameAtDisconnect,
            room_state: roomStateAtDisconnect,
            session_control_sent: sessionControlSent,
            server_session_shutdown_requested: serverSessionShutdownRequested,
            ...(guestSpatiusAvatarExperimentPropertiesRef.current || {}),
          });
        } else if (sessionEndedCapturedRecently) {
          logger.info("Skipped duplicate session_ended capture for recently ended room", {
            source,
            roomName: roomNameAtDisconnect,
            previousCapturedAtMs: lastSessionEndedCapture?.capturedAtMs,
          });
        }

        if (sessionMode !== "maths" && currentSessionDuration >= FIRST_CONVERSATION_MIN_SECONDS) {
          try {
            const hasCompletedFirstConversation =
              localStorage.getItem(FIRST_CONVERSATION_COMPLETED_KEY) === "true";

            if (!hasCompletedFirstConversation) {
              const selectedLanguageName = getLanguageNameByCode(selectedLanguageCode);
              trackFirstConversationCompleted({
                durationSeconds: currentSessionDuration,
                sessionMode: sessionMode || DEFAULT_SESSION_MODE,
                userType: user ? "authenticated" : "guest",
                selectedLanguageCode,
                selectedLanguageName,
              });
              localStorage.setItem(FIRST_CONVERSATION_COMPLETED_KEY, "true");
              logger.info("First conversation completed GA4 event tracked", {
                durationSeconds: currentSessionDuration,
              });
            }
          } catch (error) {
            logger.warn("Failed to track first conversation completion", { error });
          }
        }
      } catch (error) {
        logger.error("Failed to finish background LiveKit session cleanup:", error);
      } finally {
        if (activeSessionShutdownRef.current?.handle === shutdownHandle) {
          activeSessionShutdownRef.current = null;
        }
      }
    },
    [
      MAX_GUEST_SECONDS,
      clearAudioErrors,
      freemiumUsageState,
      isConnected,
      isRecording,
      markConnectCancelOrigin,
      markLatencyPhase,
      posthogClient,
      recordingTime,
      resetConnectionElapsedTimer,
      resetLatencyTrace,
      selectedLanguageCode,
      sessionMode,
      mathsDemoId,
      mathsFixtureRevision,
      setHasExceededGuestLimit,
      setHasExceededRegisteredLimit,
      setInteractionMode,
      showFreemiumLimitPopup,
      shutdownActiveSessionTransport,
      user,
    ]
  ); // Added dependencies

  useEffect(() => {
    liveKitCleanupCallbacksRef.current = {
      shutdownActiveSessionTransport,
      clearSpatiusPrimaryFallbackTimer,
      clearConnectionElapsedTimer,
      resetLatencyTrace,
    };
  }, [
    clearConnectionElapsedTimer,
    clearSpatiusPrimaryFallbackTimer,
    resetLatencyTrace,
    shutdownActiveSessionTransport,
  ]);

  // Toggle recording state
  const toggleRecordingImpl = useCallback(
    async (overrideSessionMode?: SessionMode): Promise<boolean> => {
      const effectiveSessionMode = normalizeSessionMode(
        overrideSessionMode || sessionMode || DEFAULT_SESSION_MODE
      );
      let supportedSessionMode: ReturnType<typeof getSupportedSessionMode>;
      try {
        supportedSessionMode = getSupportedSessionMode(effectiveSessionMode);
      } catch (error) {
        if (error instanceof UnsupportedSessionModeError) {
          logger.warn("Blocked unsupported session mode request from toggle", {
            sessionMode: effectiveSessionMode,
            overrideSessionMode,
          });
          setConnectionError(getUserFacingConnectionFailureMessage(error.message));
          setConnectionState("failed");
          connectionFailureStickyRef.current = true;
          markLatencyPhase("unsupported_session_mode", {
            sessionMode: effectiveSessionMode,
          });
          return false;
        }

        throw error;
      }

      logger.info("toggleRecording called", {
        isRecording,
        sessionMode: supportedSessionMode,
        selectedLanguageCode,
        overrideSessionMode,
      });

      if (isRecording) {
        void disconnectFromLiveKit(false, false, "toggle_recording_stop");
        return false;
      }

      startLatencyTrace(supportedSessionMode);
      // Reset retry counts for a new connection sequence
      connectRetryCountRef.current = 0;
      auth401RetryPhaseRef.current = 0;
      if (
        unresolvedStartRef.current ||
        activeConnectionDetailsRef.current?.requiresRecovery ||
        (connectionFailureStickyRef.current && activeConnectionDetailsRef.current?.metered)
      ) {
        return connectToLiveKit(supportedSessionMode);
      }
      if (!user && hasExceededGuestLimit) {
        logger.info("Guest user has exceeded their usage limit. Preventing recording start.");
        showFreemiumLimitPopup("guest");
        return false;
      }
      // connectToLiveKit already has the check, but good to have it here too for direct toggle
      if (!user && getGuestTotalUsageSeconds() >= MAX_GUEST_SECONDS) {
        setHasExceededGuestLimit(true);
        showFreemiumLimitPopup("guest");
        logger.info("Guest user has exceeded usage limit. Cannot start new session.");
        return false;
      }

      if (user && subscribed === false && hasExceededRegisteredLimit) {
        logger.info(
          "Registered user (unsubscribed) has exceeded daily usage. Cannot start new session."
        );
        showFreemiumLimitPopup("daily", freemiumUsageState);
        return false;
      }

      if (user && subscribed === false) {
        const canStartFreemiumSession = await evaluateFreemiumStartGate(supportedSessionMode, {
          allowPreSessionOffer: enableFreemiumPreSessionOffer,
        });
        if (!canStartFreemiumSession) {
          setIsRecording(false);
          resetLatencyTrace();
          return false;
        }
      }

      if (overrideSessionMode && overrideSessionMode !== sessionMode) {
        logger.info("Using override session mode for connection", {
          original: sessionMode,
          override: overrideSessionMode,
        });
      }

      if (!user && analyticsSurface === "fullscreen") {
        const exposure = getGuestSpatiusAvatarExperimentExposure?.() || null;
        if (exposure) {
          guestSpatiusAvatarExperimentPropertiesRef.current = exposure.properties;
          if (exposure.newlyExposed && posthogClient) {
            posthogClient.capture("guest_spatius_avatar_exposed", {
              ...exposure.properties,
              effective_no_avatar: noAvatar,
              session_mode: supportedSessionMode,
              selected_language_code: selectedLanguageCode,
              selected_language_level: selectedLanguageLevel,
              analytics_surface: analyticsSurface,
            });
          }
        }
      }

      await startAudioPlayback({ suppressErrorMessage: true });

      const proceeding = await connectToLiveKit(supportedSessionMode);

      // Only set isRecording to true if the connection is proceeding
      if (proceeding) {
        setIsRecording(true);

        if (
          !user &&
          (analyticsSurface === "fullscreen" || analyticsSurface === "embed") &&
          posthogClient
        ) {
          posthogClient.capture(
            analyticsSurface === "embed"
              ? embedAnalyticsEventPrefix
                ? `${embedAnalyticsEventPrefix}_session_started`
                : "embed_guest_demo_session_started"
              : "fullscreen_guest_demo_started",
            {
              session_mode: supportedSessionMode,
              selected_language_code: selectedLanguageCode,
              selected_language_name: getLanguageNameByCode(selectedLanguageCode),
              selected_language_level: selectedLanguageLevel,
              guest_seconds_used_total: getGuestTotalUsageSeconds(),
              guest_seconds_remaining_total: Math.max(
                0,
                MAX_GUEST_SECONDS - getGuestTotalUsageSeconds()
              ),
              analytics_surface: analyticsSurface,
              ...(guestSpatiusAvatarExperimentPropertiesRef.current || {}),
            }
          );
        }
      }
      return proceeding;
    },
    [
      connectToLiveKit,
      disconnectFromLiveKit,
      enableFreemiumPreSessionOffer,
      evaluateFreemiumStartGate,
      freemiumUsageState,
      hasExceededGuestLimit,
      hasExceededRegisteredLimit,
      isRecording,
      markLatencyPhase,
      resetLatencyTrace,
      selectedLanguageCode,
      setHasExceededGuestLimit,
      showFreemiumLimitPopup,
      sessionMode,
      startAudioPlayback,
      startLatencyTrace,
      subscribed,
      user,
      analyticsSurface,
      embedAnalyticsEventPrefix,
      posthogClient,
      selectedLanguageLevel,
      MAX_GUEST_SECONDS,
      getGuestSpatiusAvatarExperimentExposure,
      noAvatar,
    ]
  );

  const toggleRecording = useMemo(
    () => withLessonRefreshProtection(toggleRecordingImpl),
    [toggleRecordingImpl]
  );

  useLiveKitRecordingTimer({
    sessionKey: activeConnectionDetailsRef.current?.roomName,
    isRecording,
    user,
    authenticatedSessionUsage: activeSessionUsage,
    hasExceededGuestLimit,
    selectedLanguageCode,
    selectedLanguageLevel,
    sessionMode,
    analyticsSurface,
    posthogClient,
    maxGuestSeconds: MAX_GUEST_SECONDS,
    hasTrackedEngagedSessionRef,
    hasTrackedGuestLimitReachedRef,
    setRecordingTime,
    setHasExceededGuestLimit,
    disconnectFromLiveKit,
    logger,
  });

  // Cleanup LiveKit room on actual hook unmount
  useEffect(() => {
    return () => {
      const activeRoom = currentRoomRef.current;
      const callbacks = liveKitCleanupCallbacksRef.current;
      logger.warn("LiveKit hook unmount cleanup running", {
        isConnected: isConnectedRef.current,
        isRecording: isRecordingRef.current,
        roomName: activeRoom.name || null,
        roomState: activeRoom.state,
        ...getBrowserSessionDiagnostics(),
      });

      const inFlightShutdown = activeSessionShutdownRef.current;
      if (isConnectedRef.current || inFlightShutdown) {
        void (async () => {
          logger.warn("LiveKit hook unmount is disconnecting an active session", {
            roomName: currentRoomRef.current.name || null,
            roomState: currentRoomRef.current.state,
            ...getBrowserSessionDiagnostics(),
          });
          const cleanupPromises: Array<Promise<unknown>> = [];
          if (inFlightShutdown) {
            const upgradedHandle = callbacks?.shutdownActiveSessionTransport("component_unmount", {
              keepalive: true,
              timeoutMs: SERVER_SESSION_SHUTDOWN_KEEPALIVE_TIMEOUT_MS,
              room: inFlightShutdown.room,
              attemptId: inFlightShutdown.attemptId,
            });
            if (upgradedHandle) {
              cleanupPromises.push(upgradedHandle.cleanup);
            }
          }
          if (
            isConnectedRef.current &&
            (!inFlightShutdown || currentRoomRef.current !== inFlightShutdown.room)
          ) {
            const activeHandle = callbacks?.shutdownActiveSessionTransport("component_unmount", {
              keepalive: true,
              timeoutMs: SERVER_SESSION_SHUTDOWN_KEEPALIVE_TIMEOUT_MS,
            });
            if (activeHandle) {
              cleanupPromises.push(activeHandle.cleanup);
            }
          }
          await Promise.allSettled(cleanupPromises);
        })();
      }

      // Clear any pending connection timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (freshReconnectTimerRef.current) {
        clearTimeout(freshReconnectTimerRef.current);
        freshReconnectTimerRef.current = null;
      }
      freshReconnectInProgressRef.current = false;

      callbacks?.clearSpatiusPrimaryFallbackTimer();
      callbacks?.clearConnectionElapsedTimer();
      callbacks?.resetLatencyTrace();
    };
  }, []);

  useLiveKitAudioTrackDiagnostics({
    currentRoom,
    isRecording,
    setConnectionError,
    setAudioTrackError,
    setAudioRetryCount,
    ensureSpatiusPrimaryBaseRoomAudioPlayback,
    logger,
  });

  const dismissFreemiumPreSessionOffer = useCallback(() => {
    markPreSessionOfferDismissedToday();
    pendingPreSessionOfferModeRef.current = null;
    setShowFreemiumPreSessionOffer(false);
    onFreemiumPreSessionOfferDismissed?.();
  }, [
    onFreemiumPreSessionOfferDismissed,
    pendingPreSessionOfferModeRef,
    setShowFreemiumPreSessionOffer,
  ]);

  const continueAfterFreemiumPreSessionOffer = useCallback(() => {
    const pendingMode = pendingPreSessionOfferModeRef.current || sessionMode || "conversation";
    markPreSessionOfferDismissedToday();
    pendingPreSessionOfferModeRef.current = null;
    setShowFreemiumPreSessionOffer(false);
    void toggleRecording(pendingMode);
  }, [pendingPreSessionOfferModeRef, sessionMode, setShowFreemiumPreSessionOffer, toggleRecording]);

  const retryConnection = useCallback(
    () => connectToLiveKit(sessionMode || "conversation"),
    [connectToLiveKit, sessionMode]
  );

  return {
    room: currentRoom,
    baseRoom: room,
    isConnected,
    isRecording,
    recordingTime,
    sessionUsage: activeSessionUsage,
    connectionElapsedSeconds,
    agentState,
    connectToLiveKit,
    disconnectFromLiveKit,
    toggleRecording,
    showTimeLimitPopup,
    dismissTimeLimitPopup: () => setShowTimeLimitPopup(false),
    timeLimitPopupContext,
    freemiumUsageState,
    showFreemiumPreSessionOffer,
    dismissFreemiumPreSessionOffer,
    continueAfterFreemiumPreSessionOffer,
    hasExceededGuestLimit,
    // Export connection state information for better UI feedback
    connectionState,
    setConnectionError, // Expose setter
    connectionQuality,
    connectionError,
    retryConnection,
    // Export audio-specific error handling
    audioTrackError,
    setAudioTrackError, // Expose setter
    audioRetryCount,
    retryAudioTrack,
    clearAudioErrors,
    spatiusPrimarySession,
    handleSpatiusPrimaryConnected,
    handleSpatiusPrimaryDisconnected,
    handleSpatiusPrimaryError,
    handleSpatiusPrimaryStateChange,
  };
};

export default useLiveKitAgent;
